/**
 * Total Commerce Purchase Options Widget V3.9
 * =================================
 * Self-contained module that renders purchase/subscription/rental options
 * inside the Shopify Horizon theme product page.
 *
 * V3.9 changes from V3.8 (ported from SubMan tc-purchase-options b51e5609b):
 * - Accelerated (wallet) checkout now carries the £0 membership mandate line as
 *   shopify-additional-items[] hidden inputs. Shop Pay / Apple Pay submit the
 *   product form and never touch the cart, so the "Membership auto-add" embed
 *   never ran and the membership checkout validation hard-blocked the order with
 *   "Your cart is missing the ... item"
 * - Membership config read from the totalcommerce.membership shop metafield via
 *   our own Liquid config, so it does not depend on that app embed being enabled;
 *   falls back to the embed's #tc-membership-config element
 * - Wallet button hiding moved from the `hidden` attribute to a CSS class on the
 *   section. Horizon's product-form.js clears `hidden` on every variant:update,
 *   so the V3.3 VAT-relief guard and the V3.8 store-required guard were both
 *   defeated as soon as the shopper touched the variant picker
 * - Wallet button now also hidden on the Rent tab, whose per-customer selling
 *   plan can't be expressed as a static form field
 * - All of the above reconciled from one entry point on init, tab switch, plan
 *   selection, variant change and the fulfilment picker's change event
 *
 * V3.8 changes from V3.7:
 * - Collection store selection, rendered from the totalcommerce-fulfilment-location snippet
 * - Add to cart is blocked on every tab until a store is chosen, once the picker has loaded
 * - Writes _fulfilment_location_id plus visible Fulfilment / Collection store /
 *   Collection address properties on the rental path (the picker writes them on the form path)
 * - Accelerated checkout hidden when a store is required, since Shop Pay bypasses the form
 * - Refuses an add to cart that would mix two different collection stores in one cart
 *
 * V3.7 changes from V3.6:
 * - Subscribe/Lease: when the cart line is swapped to the vat_relief_pair
 *   variant (a separate Shopify product), the selling_plan is now remapped to
 *   that product's corresponding plan instead of submitting the displayed
 *   product's plan id (which Shopify rejects for the pair variant). The pair's
 *   plans are resolved client-side via the /variants/{id} redirect ->
 *   /products/{handle}.js, then matched on options + name, with discount as a
 *   tiebreaker and same-ordinal position as a last resort. If no plan resolves,
 *   the add-to-cart is blocked rather than adding a mismatched line.
 * - Liquid now serializes each plan's options so the match has interval/frequency
 *   data on both sides.
 *
 * V3.6 changes from V3.5:
 * - Rental selling plans now respect VAT relief
 * - When the VAT relief toggle is on, the calendar-rental plan is created with
 *   taxable:false so the backend builds it against the VAT-free variant at the
 *   ex-VAT price (÷1.2); off → taxable:true (full price, taxable variant)
 * - The Rent checkout now adds the matching variant to the cart: the ex-VAT
 *   page variant when relief is on, otherwise the taxable vat_relief_pair
 *   variant — so the cart line always matches the variant the plan was built on
 * - Prepared-plan cache is keyed by taxability; toggling relief abandons the
 *   stale plan and prefetches the correctly-taxed one
 *
 * V3.5 changes from V3.4:
 * - Adds _client_id and _merchant line item properties to all checkout paths
 * - _client_id sourced from the wuva.clientId product metafield (config.clientId)
 * - _merchant hardcoded to "Wuva"
 * - Extracted _setFormLineItemProperty helper to DRY up form-input sync
 *
 * V3.4 changes from V3.3:
 * - Adds _revenue_model line item property to all checkout paths
 * - Purchase tab → _revenue_model: "purchase"
 * - Subscribe tab (non-hire) → _revenue_model: "subscription"  
 * - Lease tab (hire types) → _revenue_model: "lease"
 * - Rent tab → _revenue_model: "rental"
 * - Property is underscore-prefixed so it's hidden from customer-facing views
 * - Visible in Shopify admin, API, and Matrixify exports
 *
 * V3.3 changes:
 * - Default VAT relief ON for eligible products (majority of customers use VAT relief)
 * - Hide accelerated checkout on VAT-relief-eligible products
 *
 * V3.2 fix:
 * - Variant swap moved from submit-time to post-ATC cart swap
 * - Form submits the original variant normally (Horizon handles selling plans correctly)
 * - After successful ATC, line item is swapped to VAT relief pair in the cart
 * - Eliminates all form interception issues with Horizon and accelerated checkout
 *
 * V3.1 fix:
 * - Cart attributes are debounced (1s after last keystroke) to avoid 429 rate limiting
 * - Flushed immediately before ATC to ensure attributes are written
 * - Only VAT-eligible products touch cart attributes
 *
 * Architecture (unchanged):
 * - Liquid serializes product data as JSON (single source of truth)
 * - JS handles ALL rendering, pricing, and state
 * - Purchase & Subscription: injects selling_plan into existing form
 * - Rental: separate ATC via fetch (multi-line-item bundle)
 * - Hooks into Horizon's ThemeEvents for variant changes
 * - Custom range calendar lazy-built on first rental tab open
 */
(function () {
  'use strict';

  /* ========================================================================
     1. CONSTANTS
     ======================================================================== */
  const VAT_RATE = 1.2;
  const TOTALCOMMERCE_API_BASE = 'https://wuva-api-prod.wuva.com/api/v1/brand-referrer';
  const MAX_RENTAL_DAYS = 89;
  const VAT_FIELD_MAX = 100;
  const MERCHANT_NAME = 'Wuva';
  // Class toggled on the widget's section to hide the accelerated/dynamic checkout (wallet)
  // button. A class, not the `hidden` attribute, because Horizon clears that attribute on every
  // variant:update — see _updateAcceleratedCheckoutVisibility.
  const HIDE_ACCEL_CLASS = 'totalcommerce-hide-accelerated-checkout';
  // Line item property key the membership mandate is stamped with. Matches the "Membership
  // auto-add" embed so the checkout validation recognises the line from either producer.
  const MEMBERSHIP_REVENUE_MODEL_KEY = '_revenueModel';
  const MEMBERSHIP_REVENUE_MODEL_VALUE = 'membership';
  const CAL_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const CAL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

  function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }
  function fmtDateLabel(d) {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  function daysBetween(a, b) {
    return Math.round((startOfDay(b) - startOfDay(a)) / 86400000);
  }
  function fmtIsoDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  function extractNumericId(gidOrId) {
    if (gidOrId == null) return '';
    var s = String(gidOrId);
    var slash = s.lastIndexOf('/');
    return slash >= 0 ? s.slice(slash + 1) : s;
  }
  function rentalDatesKey(start, end, taxable) {
    // Key includes the taxable flag: toggling VAT relief changes which Shopify
    // variant the backend builds the plan against, so a plan prepared for the
    // other taxability is stale and must be re-prepared (and abandoned).
    if (!start || !end) return null;
    return fmtIsoDate(start) + '|' + fmtIsoDate(end) + '|' + (taxable ? 't' : 'n');
  }

  /* ========================================================================
     2. SVG ICONS (inline strings)
     ======================================================================== */
  const ICONS = {
    shield: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M8 1.5L3 3.5v4c0 3.5 2.5 5.5 5 6.5 2.5-1 5-3 5-6.5v-4L8 1.5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M5.5 8l2 2 3-3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    truck: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M1 3h9v8H1zM10 6h2.5L14.5 9v2H10V6z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><circle cx="4" cy="12" r="1.3" stroke="currentColor" stroke-width="1.2"/><circle cx="12" cy="12" r="1.3" stroke="currentColor" stroke-width="1.2"/></svg>',
    refresh: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M2 8a6 6 0 0110.5-4M14 8a6 6 0 01-10.5 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M12.5 1v3h-3M3.5 15v-3h3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    box: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M2 5l6-3 6 3v6l-6 3-6-3V5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M2 5l6 3m0 0l6-3M8 8v6.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
    checkCircle: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/><path d="M5.5 8l2 2 3-3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    calendar: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M2 6.5h12" stroke="currentColor" stroke-width="1.3"/><path d="M5.5 1.5v3M10.5 1.5v3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
    user: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5" r="3" stroke="currentColor" stroke-width="1.3"/><path d="M2.5 14c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
    file: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M4 1.5h5l4 4v9H4V1.5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M9 1.5v4h4" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>',
    lock: '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="3.5" y="7" width="9" height="7" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
  };

  function icon(name) {
    return `<span class="totalcommerce-icon">${ICONS[name] || ''}</span>`;
  }

  /* ========================================================================
     3. UTILITIES
     ======================================================================== */

  function formatMoney(cents, currency, locale) {
    return new Intl.NumberFormat(locale || 'en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
    }).format(cents / 100);
  }

  // V4: VAT relief inverted. The displayed/base variant may itself be the
  // ex-VAT (tax-exempt) side — `baseIsExVat` tells us which way to convert.
  //   exVatPrice  → relief applied (the lower, tax-free price)
  //   incVatPrice → full price including 20% VAT
  function exVatPrice(priceInCents, baseIsExVat) {
    return baseIsExVat ? priceInCents : Math.round(priceInCents / VAT_RATE);
  }

  function incVatPrice(priceInCents, baseIsExVat) {
    return baseIsExVat ? Math.round(priceInCents * VAT_RATE) : priceInCents;
  }

  // Rental pricing keeps its legacy basis (API returns inc-VAT pounds), so the
  // rental display still just divides when relief is on. Intentionally left
  // unchanged by the V4 swap inversion.
  function applyVatPounds(pounds, isVatRelief) {
    if (!isVatRelief) return pounds;
    return pounds / VAT_RATE;
  }

  function parsePlanDuration(name) {
    return name;
  }

  function calcPlanPrice(variantPriceCents, adjustments) {
    if (!adjustments || !adjustments.length) return variantPriceCents;
    const adj = adjustments[0];
    if (adj.valueType === 'percentage') {
      return Math.round(variantPriceCents * (100 - adj.value) / 100);
    }
    return variantPriceCents;
  }

  function el(tag, attrs, children) {
    const e = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === 'className') e.className = v;
        else if (k === 'textContent') e.textContent = v;
        else if (k === 'innerHTML') e.innerHTML = v;
        else if (k.startsWith('on') && typeof v === 'function') e.addEventListener(k.slice(2).toLowerCase(), v);
        else if (k === 'style' && typeof v === 'object') Object.assign(e.style, v);
        else if (v !== null && v !== undefined && v !== false) e.setAttribute(k, v);
      });
    }
    if (children) {
      (Array.isArray(children) ? children : [children]).forEach(c => {
        if (c == null) return;
        e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      });
    }
    return e;
  }

  /* ========================================================================
     4. WIDGET CLASS
     ======================================================================== */
  class WuvaWidget {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.container = document.getElementById(`totalcommerce-widget-${sectionId}`);
      const configEl = document.getElementById(`totalcommerce-config-${sectionId}`);
      if (!this.container || !configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[Wuva] Failed to parse config:', e);
        return;
      }

      this.state = {
        currentTab: null,
        selectedPlanId: null,
        vatRelief: false,
        vatFormValid: false,
        vatFormTouched: { name: false, condition: false },
        rentalPricing: null,
        rentalConstraints: null,
        minRentalDays: null,
        rentalDates: { start: null, end: null },
        rentalQuickDays: 0,
        calendarLoaded: false,
        calendarInstance: null,
        // Selling plan prefetch — created in the background as soon as both
        // rental dates are picked. Cart navigation can then use the cached id
        // instantly. preparedPlan is keyed by start|end so we can detect
        // staleness on date change and abandon the stale plan via the
        // /calendar-rental/abandon endpoint instead of leaving it for the
        // nightly cleanup sweep.
        preparedPlan: null,
        preparingPlanKey: null,
        preparePlanAbortCtrl: null,
        rentalRequestInFlight: false,
      };

      // V3.1: Track the latest cart-attribute write as a resolved promise
      // so the submit handler can await it without blocking when nothing is pending.
      this._cartAttrPromise = Promise.resolve();
      this._syncDebounceTimer = null;

      // V3.7: caches for pair-product selling-plan matching, keyed by pair
      // variant id. Handle resolution and the pair product's plan list are both
      // reused across prefetch + submit so the add-to-cart isn't blocked on the
      // network in the common case.
      this._pairHandleCache = {};
      this._pairPlanCache = {};

      this.variant = this._findVariant(this.config.selectedVariantId);
      if (!this.variant) this.variant = this.config.variants[0];

      // Bundle mode (one-off): seat + optional footrest picker on a single product.
      // Partitions variants into seats/footrests from their titles and makes the
      // SEAT the main variant; footrests become companion cart lines. Falls back
      // to the standard single-picker widget if the titles can't be partitioned.
      this.bundle = null;
      if (this.config.bundleMode) this._initBundle();

      this.planPrices = {};
      this._recalcPlanPrices();

      // V4: Default VAT relief ON for any product that participates (has a pair)
      this.state.vatRelief = this._hasVatPair();
      this._abortCtrl = new AbortController();
      this.refs = {};

      // V3.9: resolved before _render because rendering selects a default plan, which reconciles
      // the express-checkout companion lines and so consults this.
      this._membershipConfig = this._readMembershipConfig();

      this._render();
      this._bindEvents();
      this._checkRentalAvailability();
      this._updateSubscriptionTabVisibility();

      // Default tab: Subscribe first (if current variant has plans), then Buy, then Rental
      if (this._variantPlans().length > 0) {
        this._switchTab('subscription');
      } else if (this.config.allowsOneTimePurchase) {
        this._switchTab('purchase');
      }

      // V3: Initial checkout blocking state
      this._updateCheckoutBlocked();

      // V3.9: reconcile the express-checkout path — wallet button visibility plus the companion
      // lines an express order has to carry. Belt-and-braces: the default-tab _switchTab above
      // already reconciled, but neither branch is guaranteed to have run.
      this._reconcileAcceleratedCheckout();

      // The collection-store picker owns the store choice; mirror its changes into our own blocked
      // state and rental button state. The express path is reconciled here too, because whether a
      // store is required is only known once the picker has loaded.
      document.addEventListener('wuva:fulfilment:change', () => {
        this._updateCheckoutBlocked();
        this._reconcileAcceleratedCheckout();
      });
    }

    /* ======================================================================
       VARIANT HELPERS
       ====================================================================== */

    _findVariant(id) {
      return this.config.variants.find(v => v.id == id) || null;
    }

    /** Extract numeric variant ID from a metafield reference (object, GID string, or number) */
    _parsePairId(val) {
      if (!val) return null;
      if (typeof val === 'object' && val !== null) val = val.id || val.admin_graphql_api_id || val;
      val = String(val);
      const match = val.match(/(\d+)$/);
      return match ? parseInt(match[1]) : null;
    }

    /* ── V4: VAT relief direction helpers ──
       Items ship as taxable inc-VAT / tax-exempt ex-VAT twins linked by the
       vat_relief_pair metafield. The displayed variant can now be EITHER side;
       `taxable === false` means the displayed variant is the ex-VAT (relief)
       one and its pair is the inc-VAT variant. Unified rule applied everywhere:
       the cart line is the EX-VAT variant iff (relief toggle ON && declaration
       complete); otherwise the INC-VAT variant. */

    /** True if the current variant participates in VAT relief (has a pair). */
    _hasVatPair() {
      return !!this._parsePairId(this.variant?.vatReliefPairId);
    }

    /** True if the displayed variant is itself the ex-VAT (tax-exempt) side. */
    _baseIsExVat() {
      return this.variant?.taxable === false;
    }

    /** Numeric variant id of the EX-VAT (relief) side of the pair. */
    _exVatVariantId() {
      if (!this.variant) return null;
      return this._baseIsExVat() ? this.variant.id : this._parsePairId(this.variant.vatReliefPairId);
    }

    /** Numeric variant id of the INC-VAT (full-price, taxable) side of the pair. */
    _incVatVariantId() {
      if (!this.variant) return null;
      return this._baseIsExVat() ? this._parsePairId(this.variant.vatReliefPairId) : this.variant.id;
    }

    /**
     * Numeric variant id of the PAIR — i.e. the OTHER product's variant
     * (always the vat_relief_pair target, regardless of which side is displayed).
     * This is the variant we swap the cart line to; when we do, its selling plan
     * must be remapped because it belongs to a different product.
     */
    _pairVariantId() {
      return this._parsePairId(this.variant?.vatReliefPairId);
    }

    /** VAT amount saved (in cents) for a given base-basis price. */
    _vatSavingCents(cents) {
      const baseEx = this._baseIsExVat();
      return incVatPrice(cents, baseEx) - exVatPrice(cents, baseEx);
    }

    /**
     * Whether a rental selling plan for the current state should be taxable.
     * A rental is non-taxable (VAT relief) only when the variant participates
     * in VAT relief AND the customer has the relief toggle on. The declaration
     * itself is enforced separately by checkout blocking (_isCheckoutBlocked),
     * so it deliberately does NOT factor in here — otherwise the prepared plan
     * would churn on every keystroke in the declaration form.
     *
     * The backend (CreateOneOffCalendarRentalSellingPlan) reads this `taxable`
     * flag to decide which Shopify variant the plan is built against:
     *   taxable: true  → variant.InternalId        (inc-VAT / taxable side)
     *   taxable: false → variant.VATFreeInternalId  (ex-VAT / relief side, price ÷1.2)
     * The cart line in _navigateToCart must then use the matching variant.
     */
    _rentalIsTaxable() {
      return !(this._hasVatPair() && this.state.vatRelief);
    }

    /**
     * Plans allocated to the current variant, intersected with the visible
     * (non-Calendar-Rentals) selling plan groups. Iterates `sellingPlanGroups`
     * so display order matches Shopify admin ordering.
     */
    _variantPlans() {
      if (!this.variant) return [];
      const allocated = new Set(
        (this.variant.sellingPlanAllocations || []).map(a => a.id)
      );
      const plans = [];
      this.config.sellingPlanGroups.forEach(g => {
        g.plans.forEach(p => {
          if (allocated.has(p.id)) plans.push(p);
        });
      });
      return plans;
    }

    _recalcPlanPrices() {
      this.planPrices = {};
      if (!this.variant) return;
      const vPrice = this.variant.price;
      const allocByPlanId = {};
      (this.variant.sellingPlanAllocations || []).forEach(a => {
        allocByPlanId[a.id] = a;
      });
      this.config.sellingPlanGroups.forEach(group => {
        group.plans.forEach(plan => {
          const alloc = allocByPlanId[plan.id];
          if (!alloc) return;
          this.planPrices[plan.id] = (alloc.perDeliveryPrice !== undefined && alloc.perDeliveryPrice !== null)
            ? alloc.perDeliveryPrice
            : calcPlanPrice(vPrice, plan.priceAdjustments);
        });
      });
    }

    /* ======================================================================
       V3.4: REVENUE MODEL HELPER
       ====================================================================== */

    /**
     * Returns the revenue model string for the current tab.
     * - purchase tab → "purchase"
     * - subscription tab + hire type → "lease"
     * - subscription tab + non-hire type → "subscription"
     * - rental tab → "rental"
     */
    _getRevenueModel() {
      switch (this.state.currentTab) {
        case 'purchase':
          return 'purchase';
        case 'subscription':
          // subscribeLabel is "Lease" for hire types, "Subscribe" for others
          return (this.config.subscribeLabel || '').toLowerCase() === 'lease' ? 'lease' : 'subscription';
        case 'rental':
          return 'rental';
        default:
          return 'purchase';
      }
    }

    /* ======================================================================
       PRICING
       ====================================================================== */

    _fmt(cents) {
      return formatMoney(cents, this.config.shopCurrency, this.config.shopLocale);
    }

    _adjustedPrice(cents) {
      const baseEx = this._baseIsExVat();
      return this.state.vatRelief ? exVatPrice(cents, baseEx) : incVatPrice(cents, baseEx);
    }

    _adjustedPricePounds(pounds) {
      return applyVatPounds(pounds, this.state.vatRelief);
    }

    _fmtPounds(pounds) {
      return `£${this._adjustedPricePounds(pounds).toFixed(2)}`;
    }

    /* ======================================================================
       RENDER
       ====================================================================== */

    _render() {
      const frag = document.createDocumentFragment();

      // Bundle mode: seat + footrest pickers above the tabs (replaces the theme
      // variant-picker, which the bundle template omits).
      if (this.config.bundleMode && this.bundle) {
        frag.appendChild(this._renderBundlePickers());
      }

      // Tab navigation
      frag.appendChild(this._renderTabs());

      // Panels
      if (this.config.allowsOneTimePurchase) {
        frag.appendChild(this._renderBuyPanel());
      }
      if (this.config.hasSellingPlans) {
        frag.appendChild(this._renderSubscriptionPanel());
      }
      frag.appendChild(this._renderRentalPanel());

      // V3: VAT Relief section (banner + form) — below panels
      if (this._hasVatPair()) {
        frag.appendChild(this._renderVatSection());
      }

      // Rental ATC button — below VAT section so user fills form then clicks ATC
      if (this.refs.rentalAtcBtn) {
        frag.appendChild(this.refs.rentalAtcBtn);
      }

      // Bundle mode: add-on breakdown (footrest lines + combined total), shown
      // just above the buy buttons whenever at least one footrest is selected.
      if (this.config.bundleMode && this.bundle) {
        const addon = el('div', { className: 'totalcommerce-addon-breakdown', style: { display: 'none' } });
        frag.appendChild(addon);
        this.refs.addonBreakdown = addon;
      }

      // Error message
      const errEl = el('div', { className: 'totalcommerce-error', 'data-visible': 'false' });
      frag.appendChild(errEl);
      this.refs.error = errEl;

      this.container.innerHTML = '';
      this.container.appendChild(frag);
    }

    /* ── VAT Relief Section (banner + declaration form) ── */

    _renderVatSection() {
      const section = el('div', { className: 'totalcommerce-vat-section' });

      // Banner
      section.appendChild(this._renderVatBanner());

      // Declaration form (animated)
      const formWrap = el('div', {
        className: 'totalcommerce-vat-form',
        'data-open': this.state.vatRelief ? 'true' : 'false',
      });

      const formInner = el('div', { className: 'totalcommerce-vat-form-inner' });

      formInner.appendChild(el('div', {
        className: 'totalcommerce-vat-form-intro',
        textContent: 'To claim VAT relief, confirm you have a qualifying disability or long-term condition.',
      }));

      // Name field
      const nameField = el('div', { className: 'totalcommerce-vat-field' });
      const nameLabel = el('label', { className: 'totalcommerce-vat-label' });
      nameLabel.innerHTML = `${icon('user')} Full name of eligible person`;
      nameField.appendChild(nameLabel);

      const nameInput = el('input', {
        className: 'totalcommerce-vat-input',
        type: 'text',
        placeholder: 'Enter full name',
        maxlength: String(VAT_FIELD_MAX),
        'data-error': 'false',
      });
      nameInput.addEventListener('input', () => this._onVatFieldChange());
      nameInput.addEventListener('blur', () => {
        this.state.vatFormTouched.name = true;
        this._validateVatField('name');
      });
      nameInput.addEventListener('focus', () => { nameInput.dataset.error = 'false'; });
      nameField.appendChild(nameInput);

      const nameFooter = el('div', { className: 'totalcommerce-vat-field-footer' });
      const nameError = el('span', { className: 'totalcommerce-vat-field-error', style: { display: 'none' }, textContent: 'Required' });
      const nameCount = el('span', { className: 'totalcommerce-vat-field-count', textContent: `0/${VAT_FIELD_MAX}` });
      nameFooter.append(nameError, nameCount);
      nameField.appendChild(nameFooter);
      formInner.appendChild(nameField);

      // Condition field
      const condField = el('div', { className: 'totalcommerce-vat-field' });
      const condLabel = el('label', { className: 'totalcommerce-vat-label' });
      condLabel.innerHTML = `${icon('file')} Qualifying condition`;
      condField.appendChild(condLabel);

      const condInput = el('textarea', {
        className: 'totalcommerce-vat-input totalcommerce-vat-textarea',
        placeholder: 'Describe the disability or long-term condition',
        maxlength: String(VAT_FIELD_MAX),
        rows: '2',
        'data-error': 'false',
      });
      condInput.addEventListener('input', () => this._onVatFieldChange());
      condInput.addEventListener('blur', () => {
        this.state.vatFormTouched.condition = true;
        this._validateVatField('condition');
      });
      condInput.addEventListener('focus', () => { condInput.dataset.error = 'false'; });
      condField.appendChild(condInput);

      const condFooter = el('div', { className: 'totalcommerce-vat-field-footer' });
      const condError = el('span', { className: 'totalcommerce-vat-field-error', style: { display: 'none' }, textContent: 'Required' });
      const condCount = el('span', { className: 'totalcommerce-vat-field-count', textContent: `0/${VAT_FIELD_MAX}` });
      condFooter.append(condError, condCount);
      condField.appendChild(condFooter);
      formInner.appendChild(condField);

      // Status line
      const status = el('div', {
        className: 'totalcommerce-vat-status',
        'data-complete': 'false',
      });
      status.innerHTML = `${icon('lock')} Declaration stored securely with your order`;
      formInner.appendChild(status);

      formWrap.appendChild(formInner);
      section.appendChild(formWrap);

      // Checkout blocked message (below form, above buttons)
      const blockedMsg = el('div', {
        className: 'totalcommerce-checkout-blocked-msg',
        'data-visible': this.state.vatRelief ? 'true' : 'false',
      });
      blockedMsg.innerHTML = `${icon('shield')} Complete the declaration to keep VAT relief — otherwise VAT is added at checkout`;
      section.appendChild(blockedMsg);

      this.refs.vatSection = section;
      this.refs.vatFormWrap = formWrap;
      this.refs.vatNameInput = nameInput;
      this.refs.vatCondInput = condInput;
      this.refs.vatNameError = nameError;
      this.refs.vatCondError = condError;
      this.refs.vatNameCount = nameCount;
      this.refs.vatCondCount = condCount;
      this.refs.vatStatus = status;
      this.refs.vatBlockedMsg = blockedMsg;

      return section;
    }

    _renderVatBanner() {
      const banner = el('div', {
        className: 'totalcommerce-vat-banner',
        'data-active': this.state.vatRelief ? 'true' : 'false',
      });

      const left = el('div', { className: 'totalcommerce-vat-banner-left' });
      left.appendChild(el('span', { className: 'totalcommerce-icon', innerHTML: ICONS.shield, style: { color: '#235c4d' } }));
      left.appendChild(el('span', { className: 'totalcommerce-vat-banner-label', textContent: 'VAT Relief eligible' }));

      const saving = el('span', { className: 'totalcommerce-vat-saving' });
      let initPrice = this.variant.price;
      if (this.state.currentTab === 'subscription' && this.state.selectedPlanId && this.planPrices[this.state.selectedPlanId] !== undefined) {
        initPrice = this.planPrices[this.state.selectedPlanId];
      }
      const savingAmount = this._vatSavingCents(initPrice);
      saving.textContent = `Saving ${this._fmt(savingAmount)}`;
      left.appendChild(saving);
      this.refs.vatSaving = saving;

      banner.appendChild(left);

      // Toggle
      const toggle = el('button', { type: 'button',
        className: 'totalcommerce-toggle',
        'data-on': this.state.vatRelief ? 'true' : 'false',
        'aria-label': 'Toggle VAT Relief',
      });
      toggle.appendChild(el('span', { className: 'totalcommerce-toggle-knob' }));
      toggle.addEventListener('click', () => this._toggleVatRelief());
      banner.appendChild(toggle);

      this.refs.vatBanner = banner;
      this.refs.vatToggle = toggle;
      return banner;
    }

    /* ── VAT Relief Logic ── */

    _toggleVatRelief() {
      this.state.vatRelief = !this.state.vatRelief;
      const on = this.state.vatRelief;

      // Update toggle + banner
      this.refs.vatToggle.dataset.on = on ? 'true' : 'false';
      this.refs.vatBanner.dataset.active = on ? 'true' : 'false';

      // Show/hide form
      if (this.refs.vatFormWrap) {
        this.refs.vatFormWrap.dataset.open = on ? 'true' : 'false';
      }

      // Clear form if toggling off
      if (!on) {
        if (this.refs.vatNameInput) this.refs.vatNameInput.value = '';
        if (this.refs.vatCondInput) this.refs.vatCondInput.value = '';
        this.state.vatFormTouched = { name: false, condition: false };
        this.state.vatFormValid = false;
        if (this.refs.vatNameError) this.refs.vatNameError.style.display = 'none';
        if (this.refs.vatCondError) this.refs.vatCondError.style.display = 'none';
        if (this.refs.vatNameInput) this.refs.vatNameInput.dataset.error = 'false';
        if (this.refs.vatCondInput) this.refs.vatCondInput.dataset.error = 'false';
        if (this.refs.vatNameCount) this.refs.vatNameCount.textContent = `0/${VAT_FIELD_MAX}`;
        if (this.refs.vatCondCount) this.refs.vatCondCount.textContent = `0/${VAT_FIELD_MAX}`;
        this._updateVatStatus();
      }

      this._updateVatSaving();
      this._updateAllPrices();
      this._updateCheckoutBlocked();

      // V3.1: Sync cart attributes immediately on toggle (not debounced)
      this._syncCartAttributes(true);

      // V3.6: Toggling relief flips the rental plan's taxability, so any plan
      // prepared for the previous state is now stale. Re-prepare (which abandons
      // the old plan) when a rental date range is active; a no-op otherwise.
      this._prepareRentalPlan();

      // V3.7: relief direction decides whether we swap to the pair at submit, so
      // make sure the pair-product plans are warmed.
      this._preparePairPlan();
    }

    /* ── V3.1: Unified cart attribute sync (debounced) ── */
    _syncCartAttributes(immediate = false) {
      if (!this._hasVatPair()) {
        this._cartAttrPromise = Promise.resolve();
        return;
      }

      if (this._syncDebounceTimer) {
        clearTimeout(this._syncDebounceTimer);
        this._syncDebounceTimer = null;
      }

      const doSync = () => {
        if (this.state.vatRelief && this.state.vatFormValid) {
          this._cartAttrPromise = this._setCartAttributes();
        } else {
          this._cartAttrPromise = this._clearCartAttributes();
        }
      };

      if (immediate) {
        doSync();
      } else {
        this._syncDebounceTimer = setTimeout(doSync, 1000);
      }
    }

    async _clearCartAttributes() {
      const body = JSON.stringify({
        attributes: {
          'VAT Relief Claimed': '',
          'VAT Relief Claim Type': '',
          'VAT Relief Name': '',
          'VAT Relief Disability': '',
          'VAT Relief Timestamp': '',
        },
      });
      for (let attempt = 0; attempt <= 2; attempt++) {
        try {
          const resp = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          });
          if (resp.status === 429) {
            await new Promise(r => setTimeout(r, (attempt + 1) * 1000));
            continue;
          }
          return;
        } catch (e) {
          console.error('[Wuva] Failed to clear cart attributes:', e);
          return;
        }
      }
    }

    _onVatFieldChange() {
      const name = (this.refs.vatNameInput?.value || '').trim();
      const cond = (this.refs.vatCondInput?.value || '').trim();

      if (this.refs.vatNameCount) this.refs.vatNameCount.textContent = `${this.refs.vatNameInput?.value?.length || 0}/${VAT_FIELD_MAX}`;
      if (this.refs.vatCondCount) this.refs.vatCondCount.textContent = `${this.refs.vatCondInput?.value?.length || 0}/${VAT_FIELD_MAX}`;

      this.state.vatFormValid = name.length > 0 && cond.length > 0;
      this._updateVatStatus();
      this._updateCheckoutBlocked();

      this._syncCartAttributes();
    }

    _validateVatField(field) {
      if (field === 'name' && this.state.vatFormTouched.name) {
        const empty = !(this.refs.vatNameInput?.value || '').trim();
        if (this.refs.vatNameError) this.refs.vatNameError.style.display = empty ? '' : 'none';
        if (this.refs.vatNameInput) this.refs.vatNameInput.dataset.error = empty ? 'true' : 'false';
      }
      if (field === 'condition' && this.state.vatFormTouched.condition) {
        const empty = !(this.refs.vatCondInput?.value || '').trim();
        if (this.refs.vatCondError) this.refs.vatCondError.style.display = empty ? '' : 'none';
        if (this.refs.vatCondInput) this.refs.vatCondInput.dataset.error = empty ? 'true' : 'false';
      }
    }

    _updateVatStatus() {
      if (!this.refs.vatStatus) return;
      if (this.state.vatFormValid) {
        this.refs.vatStatus.dataset.complete = 'true';
        this.refs.vatStatus.innerHTML = `${icon('checkCircle')} Declaration complete`;
      } else {
        this.refs.vatStatus.dataset.complete = 'false';
        this.refs.vatStatus.innerHTML = `${icon('lock')} Declaration stored securely with your order`;
      }
    }

    _updateVatSaving() {
      if (!this.refs.vatSaving || !this.variant) return;

      if (this.state.currentTab === 'rental') {
        const { start, end } = this.state.rentalDates;
        if (!start || !end || !this.state.rentalPricing) {
          this.refs.vatSaving.textContent = '';
          return;
        }
        const days = Math.ceil((end - start) / 86400000) + 1;
        if (days <= 0) { this.refs.vatSaving.textContent = ''; return; }
        const rp = this.state.rentalPricing;
        const totalPounds = rp.fixedCosts + (rp.dayRate * days);
        const savingPounds = totalPounds - (totalPounds / VAT_RATE);
        this.refs.vatSaving.textContent = `Saving £${savingPounds.toFixed(2)}`;
        return;
      }

      let activePrice = this.variant.price;
      if (this.state.currentTab === 'subscription' && this.state.selectedPlanId && this.planPrices[this.state.selectedPlanId] !== undefined) {
        activePrice = this.planPrices[this.state.selectedPlanId];
      }
      const savingAmount = this._vatSavingCents(activePrice);
      const suffix = this.state.currentTab === 'subscription' ? '/mo' : '';
      this.refs.vatSaving.textContent = `Saving ${this._fmt(savingAmount)}${suffix}`;
    }

    /* ── Checkout Blocking (V4: purchase/subscription never block) ── */

    _isCheckoutBlocked() {
      // A required collection store with nothing chosen blocks every tab. The gate only engages once
      // the picker has stores in hand, so an API failure cannot make the product unbuyable.
      if (typeof window.wuvaFulfilmentNeedsStore === 'function' && window.wuvaFulfilmentNeedsStore()) {
        return true;
      }

      // V4: only the rental tab still hard-blocks on an incomplete declaration
      // (rentals use a separate vat-free mechanism). Purchase & subscription
      // never block — an incomplete declaration falls back to the inc-VAT pair.
      if (this.state.currentTab === 'rental') {
        return this.state.vatRelief && !this.state.vatFormValid;
      }
      return false;
    }

    _updateCheckoutBlocked() {
      const blocked = this._isCheckoutBlocked();

      if (blocked) {
        document.body.classList.add('totalcommerce-checkout-blocked');
      } else {
        document.body.classList.remove('totalcommerce-checkout-blocked');
      }

      // Non-blocking reminder on purchase/subscription: relief is on but the
      // declaration isn't complete, so the cart will fall back to inc-VAT.
      const remind = this.state.vatRelief && !this.state.vatFormValid && this.state.currentTab !== 'rental';
      if (this.refs.vatBlockedMsg) {
        this.refs.vatBlockedMsg.dataset.visible = remind ? 'true' : 'false';
      }

      this._updateRentalButtonState();
    }

    /* ── Variant Swap + Cart Attributes (V3.2) ── */

    /**
     * V3.5: Set or remove a `properties[<key>]` hidden input on the product form.
     * Pass null/undefined/'' for value to remove the input.
     */
    _setFormLineItemProperty(form, key, value) {
      const name = `properties[${key}]`;
      const existing = form.querySelector(`input[name="${name}"]`);

      if (value === null || value === undefined || value === '') {
        if (existing) existing.remove();
        return;
      }

      if (existing) {
        existing.value = value;
      } else {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      }
    }

    /**
     * V3.5: Sync selling plan AND line item properties into the form.
     * Runs on every tab switch and plan change so the form always
     * has the correct hidden inputs before Horizon reads them.
     *
     * Line item properties synced:
     *   _revenue_model — purchase / subscription / lease (see _getRevenueModel)
     *   _client_id     — from wuva.clientId product metafield (config.clientId)
     *   _merchant      — always "Wuva"
     *
     * On the rental tab these properties are removed from the form because
     * the rental flow writes them directly via _handleRentalAddToCart().
     */
    _syncSellingPlanToForm() {
      const formComponent = this._getProductForm();
      if (!formComponent) return;
      const form = formComponent.querySelector('form');
      if (!form) return;

      // ── Selling plan input ──
      const existingSP = form.querySelector('input[name="selling_plan"]');

      if (this.state.currentTab === 'subscription' && this.state.selectedPlanId) {
        if (existingSP) {
          existingSP.value = this.state.selectedPlanId;
        } else {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          input.value = this.state.selectedPlanId;
          form.appendChild(input);
        }
      } else {
        if (existingSP) existingSP.remove();
      }

      // ── Line item properties ──
      const isRental = this.state.currentTab === 'rental';
      this._setFormLineItemProperty(form, '_revenue_model', isRental ? null : this._getRevenueModel());
      this._setFormLineItemProperty(form, '_client_id',     isRental ? null : (this.config.clientId || null));
      this._setFormLineItemProperty(form, '_merchant',      isRental ? null : MERCHANT_NAME);
    }

    async _setCartAttributes() {
      if (!this.state.vatRelief || !this.state.vatFormValid) return;

      const name = (this.refs.vatNameInput?.value || '').trim();
      const cond = (this.refs.vatCondInput?.value || '').trim();

      const attributes = {
        'VAT Relief Claimed': 'true',
        'VAT Relief Claim Type': 'individual',
        'VAT Relief Name': name,
        'VAT Relief Disability': cond,
        'VAT Relief Timestamp': new Date().toISOString(),
      };

      const body = JSON.stringify({ attributes });
      for (let attempt = 0; attempt <= 2; attempt++) {
        try {
          const resp = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          });
          if (resp.status === 429) {
            await new Promise(r => setTimeout(r, (attempt + 1) * 1000));
            continue;
          }
          if (!resp.ok) {
            console.error('[Wuva] Cart attribute update failed:', resp.status);
          }
          return;
        } catch (e) {
          console.error('[Wuva] Failed to set cart attributes:', e);
          return;
        }
      }
    }

    /* ======================================================================
       V3.9: ACCELERATED (WALLET) CHECKOUT
       Shop Pay / Apple Pay / Google Pay submit the theme's product form
       directly and never touch the cart, so nothing the widget does after an
       add-to-cart can run on this path. Two consequences are handled here:
       visibility (hide the button wherever an express purchase would be wrong)
       and companion lines (carry the extra cart lines an express order needs
       as shopify-additional-items form fields).
       ====================================================================== */

    /**
     * Inject (once) the stylesheet that hides the wallet button whenever
     * HIDE_ACCEL_CLASS is present on an ancestor.
     *
     * A style rule rather than an imperative `hidden` attribute because
     * Horizon's product-form.js clears that attribute on every variant:update:
     *
     *   acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
     *
     * so anything we set is wiped the moment the shopper touches the variant
     * picker. Targets the Shopify-standard custom elements as well as Horizon's
     * own wrapper, so it survives a theme upgrade that renames one of them.
     */
    _ensureAcceleratedHideStyle() {
      if (document.getElementById('totalcommerce-accel-hide-style')) return;
      const style = document.createElement('style');
      style.id = 'totalcommerce-accel-hide-style';
      style.textContent =
        `.${HIDE_ACCEL_CLASS} shopify-payment-button,` +
        `.${HIDE_ACCEL_CLASS} shopify-accelerated-checkout,` +
        `.${HIDE_ACCEL_CLASS} .shopify-payment-button,` +
        `.${HIDE_ACCEL_CLASS} .accelerated-checkout-block` +
        ' { display: none !important; }';
      document.head.appendChild(style);
    }

    /** True if any variant of this product is one half of a VAT-relief pair. */
    _participatesInVatRelief() {
      return this.config.variants.some(v => !!this._parsePairId(v.vatReliefPairId));
    }

    /**
     * Show or hide the wallet button for the current state by toggling
     * HIDE_ACCEL_CLASS on the widget's section. Three reasons to hide it:
     *
     *   1. VAT relief: the cart-line swap to the vat_relief_pair variant runs
     *      after add-to-cart, which an express purchase never reaches, so the
     *      order would take the displayed variant with no declaration behind it.
     *   2. Collection store required: the picker's choice is written into the
     *      form and the cart, and an express purchase can skip it entirely.
     *   3. Rental tab: a calendar rental's selling plan is created per customer
     *      per date range on demand, so it can't be expressed as a static form
     *      field — an express purchase there would silently become a full-price
     *      one-off of the base variant.
     *
     * Every other case carries its companion lines via
     * _syncAdditionalItemsToForm, so the button stays available.
     */
    _updateAcceleratedCheckoutVisibility() {
      this._ensureAcceleratedHideStyle();

      const scope = this.container.closest('.shopify-section') || this._getProductForm();
      if (!scope) return;

      const hide =
        this.config.bundleMode ||
        this._participatesInVatRelief() ||
        !!window.wuvaFulfilmentRequired ||
        this.state.currentTab === 'rental';

      scope.classList.toggle(HIDE_ACCEL_CLASS, hide);
    }

    /**
     * Membership-mandate config, or null when memberships are off (which
     * suppresses all mandate injection).
     *
     * Preferred source is our own element, serialised straight from the
     * totalcommerce.membership shop metafield, so this works whether or not the
     * "Membership auto-add" app embed is enabled — that embed is off by default
     * and enabling it is a merchant setup step, whereas the mandate line has to
     * be correct on every page load. Falls back to the embed's own
     * #tc-membership-config, which has the same shape.
     *
     * Liquid hands a json-typed metafield back as an object while the embed
     * serialises it as a string, so both shapes are accepted.
     */
    _readMembershipConfig() {
      const elem =
        document.getElementById(`totalcommerce-membership-config-${this.sectionId}`) ||
        document.getElementById('tc-membership-config');
      if (!elem) return null;

      let membership;
      let revenueModelKey = MEMBERSHIP_REVENUE_MODEL_KEY;
      let revenueModelValue = MEMBERSHIP_REVENUE_MODEL_VALUE;

      try {
        const parsed = JSON.parse(elem.textContent);
        membership = parsed.membership;
        revenueModelKey = parsed.revenueModelKey || revenueModelKey;
        revenueModelValue = parsed.revenueModelValue || revenueModelValue;
        if (typeof membership === 'string') membership = JSON.parse(membership);
      } catch (e) {
        console.error('[Wuva] Membership config parse error:', e);
        return null;
      }
      if (!membership) return null;

      return {
        variantId: membership.variantId,
        sellingPlanId: membership.sellingPlanId,
        enabled: membership.enabled === true,
        revenueModelKey,
        revenueModelValue,
      };
    }

    /**
     * The £0 membership-mandate line, or null when it isn't required.
     *
     * Mirrors the checkout validation's composition rule (SubMan
     * membership-checkout-validation): the mandate is required exactly when the
     * main line is a Total-Commerce-managed selling-plan line, which the
     * validation recognises by the selling plan plus the revenue-model line
     * property _syncSellingPlanToForm stamps. On the normal add-to-cart path the
     * "Membership auto-add" embed reconciles this line after the add; on the
     * express path there is no cart mutation for it to observe, so the mandate
     * has to ride along in the same submission or checkout hard-fails with
     * "Your cart is missing the ... item".
     *
     * The rental tab is deliberately absent: its plan is created per customer
     * on demand, so the wallet button is hidden there entirely rather than
     * carrying a mandate for a main line that can't be expressed.
     */
    _buildMembershipItem() {
      const m = this._membershipConfig;
      if (!m || m.enabled !== true || !m.variantId || !m.sellingPlanId) return null;

      const mainIsPlanLine =
        this.state.currentTab === 'subscription' && !!this.state.selectedPlanId;
      if (!mainIsPlanLine) return null;

      const properties = {};
      properties[m.revenueModelKey] = m.revenueModelValue;

      return {
        id: extractNumericId(m.variantId),
        quantity: 1,
        selling_plan: extractNumericId(m.sellingPlanId),
        properties,
      };
    }

    /**
     * The full set of companion lines an express checkout must carry alongside
     * the main product line. The main line itself is the theme form's own
     * variant + selling_plan + line item properties, injected separately by
     * _syncSellingPlanToForm.
     */
    _buildAdditionalItems() {
      const items = [];
      const membershipItem = this._buildMembershipItem();
      if (membershipItem) items.push(membershipItem);
      return items;
    }

    /**
     * Reconcile the companion lines into the theme's product form as
     * shopify-additional-items[i][...] hidden inputs, which Shopify's wallet
     * buttons read off the associated product form and carry into the express
     * order.
     *
     * Rebuilt from scratch on every relevant state change into a single owned
     * container so it can never accumulate stale inputs. Indices start at 1, as
     * Shopify requires.
     *
     * Coexistence with the normal path: /cart/add ignores unknown
     * shopify-additional-items params, and the membership embed's reconcile is
     * idempotent (it computes add/remove/pin/none against the live cart), so a
     * normal add-to-cart can't end up with the mandate line twice.
     */
    _syncAdditionalItemsToForm() {
      const formComponent = this._getProductForm();
      if (!formComponent) return;
      const form = formComponent.querySelector('form');
      if (!form) return;

      let container = form.querySelector('[data-totalcommerce-additional-items]');
      if (!container) {
        container = document.createElement('div');
        container.setAttribute('data-totalcommerce-additional-items', '1');
        container.hidden = true;
        form.appendChild(container);
      }
      container.innerHTML = '';

      this._buildAdditionalItems().forEach((item, idx) => {
        // Shopify requires additional items to be indexed starting at 1.
        const base = `shopify-additional-items[${idx + 1}]`;

        const addInput = (name, value) => {
          if (value === null || value === undefined || value === '') return;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = String(value);
          container.appendChild(input);
        };

        addInput(`${base}[id]`, item.id);
        addInput(`${base}[quantity]`, item.quantity);
        if (item.selling_plan) addInput(`${base}[selling_plan]`, item.selling_plan);
        // [properties] is a single field holding a JSON-encoded object, NOT nested
        // [properties][key] inputs. Shopify's portable-wallets bundle JSON.parses this one
        // value; given nested inputs it parses the first raw value instead and throws
        // "Could not read additional items from the product form", discarding every
        // additional item — so the companion lines silently never reach the express order.
        if (item.properties && Object.keys(item.properties).length > 0) {
          addInput(`${base}[properties]`, JSON.stringify(item.properties));
        }
      });
    }

    /**
     * Single reconciliation entry point for the express-checkout path. Call
     * after anything that could change either the wallet button's visibility or
     * the companion lines: init, tab switch, plan selection, variant change,
     * and the fulfilment picker's change event.
     */
    _reconcileAcceleratedCheckout() {
      this._updateAcceleratedCheckoutVisibility();
      this._syncAdditionalItemsToForm();
    }

    /* ======================================================================
       V3.7: PAIR-PRODUCT SELLING-PLAN MATCHING
       The vat_relief_pair metafield is a plain integer (the pair variant id),
       so Liquid can't dereference it. We resolve it client-side: /variants/{id}
       301-redirects to /products/{handle}, then /products/{handle}.js gives the
       pair variant's own plans. No Storefront API and no extra metafields.
       ====================================================================== */

    /** Find a plan definition (with options + price adjustments) in the config. */
    _findConfigPlan(planId) {
      for (const g of this.config.sellingPlanGroups || []) {
        for (const p of g.plans || []) {
          if (p.id == planId) return p;
        }
      }
      return null;
    }

    /** Position of a plan among the current variant's allocated plans (group order). */
    _configPlanOrdinal(planId) {
      return this._variantPlans().findIndex(p => p.id == planId);
    }

    /**
     * Resolve a pair variant id to its product handle via the storefront
     * /variants/{id} redirect. Cached. Returns null if it can't be resolved.
     */
    async _resolvePairHandle(pairVariantId) {
      if (!pairVariantId) return null;
      if (this._pairHandleCache[pairVariantId]) return this._pairHandleCache[pairVariantId];

      let resp = null;
      try {
        resp = await fetch(`/variants/${pairVariantId}`, { method: 'HEAD' });
      } catch (e) { resp = null; }
      // Some setups don't honour HEAD on this route — fall back to GET.
      if (!resp || !resp.url || !/\/products\//.test(resp.url)) {
        try {
          resp = await fetch(`/variants/${pairVariantId}`);
        } catch (e) { return null; }
      }
      const m = resp && resp.url ? resp.url.match(/\/products\/([^/?#]+)/) : null;
      const handle = m ? m[1] : null;
      if (handle) this._pairHandleCache[pairVariantId] = handle;
      return handle;
    }

    /**
     * Load the pair variant's own selling plans from /products/{handle}.js.
     * Returns an array of { id, name, options, priceAdjustments, ordinal } for
     * the plans allocated to that variant, or null on any failure. The in-flight
     * promise is cached per pair-variant id; failures are not cached so a later
     * call (e.g. at submit) retries.
     */
    async _loadPairPlans(pairVariantId) {
      if (!pairVariantId) return null;
      if (this._pairPlanCache[pairVariantId]) return this._pairPlanCache[pairVariantId];

      const promise = (async () => {
        const handle = await this._resolvePairHandle(pairVariantId);
        if (!handle) return null;
        let product;
        try {
          const resp = await fetch(`/products/${handle}.js`);
          if (!resp.ok) return null;
          product = await resp.json();
        } catch (e) {
          console.error('[Wuva] Failed to fetch pair product:', e);
          return null;
        }
        const variant = (product.variants || []).find(v => String(v.id) === String(pairVariantId));
        if (!variant) return null;
        const allocated = new Set((variant.selling_plan_allocations || []).map(a => a.selling_plan_id));
        const plans = [];
        (product.selling_plan_groups || []).forEach(g => {
          (g.selling_plans || []).forEach(sp => {
            if (!allocated.has(sp.id)) return;
            plans.push({
              id: sp.id,
              name: sp.name,
              options: (sp.options || []).map(o => ({ name: o.name, value: o.value })),
              priceAdjustments: (sp.price_adjustments || []).map(a => ({ valueType: a.value_type, value: a.value })),
              ordinal: plans.length,
            });
          });
        });
        return plans.length ? plans : null;
      })();

      this._pairPlanCache[pairVariantId] = promise;
      const result = await promise;
      if (!result) delete this._pairPlanCache[pairVariantId]; // allow retry on failure
      return result;
    }

    /**
     * Match the displayed product's selected plan to the corresponding plan on
     * the pair product. Match key is options + name; ties break on discount, then
     * on same ordinal position. Returns the pair plan id, or null if nothing
     * resolves (the caller must then block the add-to-cart).
     */
    _matchPairPlan(selectedPlanId, pairPlans) {
      if (!pairPlans || !pairPlans.length) return null;
      const sel = this._findConfigPlan(selectedPlanId);
      if (!sel) return null;

      const normOpts = (opts) => (opts || [])
        .map(o => `${String(o.name).trim().toLowerCase()}=${String(o.value).trim().toLowerCase()}`)
        .sort().join('|');
      const normName = (n) => String(n || '').trim().toLowerCase();
      const normDisc = (adjs) => (adjs || [])
        .map(a => `${a.valueType}:${a.value}`).sort().join('|');

      const selOpts = normOpts(sel.options);
      const selName = normName(sel.name);
      const selDisc = normDisc(sel.priceAdjustments);

      // 1. primary: options + name
      let candidates = pairPlans.filter(p => normOpts(p.options) === selOpts && normName(p.name) === selName);
      // relax to options-only if the twins' plan names differ
      if (candidates.length === 0) {
        candidates = pairPlans.filter(p => normOpts(p.options) === selOpts);
      }

      if (candidates.length === 1) return candidates[0].id;

      if (candidates.length > 1) {
        // 2. tiebreak: discount
        const byDisc = candidates.filter(p => normDisc(p.priceAdjustments) === selDisc);
        const pool = byDisc.length ? byDisc : candidates;
        if (pool.length === 1) return pool[0].id;
        // 3. last resort: same ordinal position
        const ord = this._configPlanOrdinal(selectedPlanId);
        const byOrd = pool.find(p => p.ordinal === ord);
        return (byOrd || pool[0]).id;
      }

      // no options match at all → last resort: same ordinal across all pair plans
      const ord = this._configPlanOrdinal(selectedPlanId);
      const byOrd = pairPlans.find(p => p.ordinal === ord);
      return byOrd ? byOrd.id : null;
    }

    /** Best-effort prefetch of the pair product's plans (Subscribe tab only). */
    _preparePairPlan() {
      if (!this._hasVatPair()) return;
      if (this.state.currentTab !== 'subscription') return;
      const pairVariantId = this._pairVariantId();
      if (!pairVariantId) return;
      // Fire and forget; the result is cached for the submit path.
      this._loadPairPlans(pairVariantId);
    }

    /**
     * Refuses an add-to-cart that would put two different collection stores in one cart. The whole
     * cart ships to one store, so a mixed cart cannot be fulfilled.
     *
     * Client-side only by design. Accelerated checkout is hidden for these products, so the
     * realistic remaining path is a hand-built cart permalink; such an order still ingests
     * correctly, it just may span two stores on one contract.
     *
     * Returns the conflicting store name, or null when the cart is consistent.
     */
    async _findConflictingCartStore() {
      const fulfilment = window.wuvaFulfilment;
      if (!fulfilment || !fulfilment.locationId) return null;

      try {
        const resp = await fetch('/cart.js');
        if (!resp.ok) return null;
        const cart = await resp.json();

        for (const item of cart.items || []) {
          const props = item.properties || {};
          const existingId = props._fulfilment_location_id;
          if (existingId && existingId !== fulfilment.locationId) {
            return props['Collection store'] || 'another store';
          }
        }
      } catch (e) {
        // A cart read failure must not block the sale.
        console.error('[Wuva] Could not check the cart for a conflicting store:', e);
      }

      return null;
    }

    /**
     * Returns true when the add to cart was refused because the cart is committed to another store.
     * Shared by the form-submit and rental paths so the message stays in one place.
     */
    async _refuseIfCartStoreConflicts() {
      const conflict = await this._findConflictingCartStore();
      if (!conflict) return false;

      this._showError(
        'Your cart is already set to collect from ' + conflict +
        '. Check out that order first, or empty your cart to collect from a different store.'
      );
      return true;
    }

    /* ======================================================================
       BUNDLE MODE (one-off): seat + optional elevating footrest(s)
       The widget's main variant is the SEAT; selected footrests are companion
       cart lines added in the SAME /cart/add.js call, on the same term (Lease),
       same dates (Rent) or outright (Buy). Everything here is gated on
       this.config.bundleMode so other products are untouched.
       ====================================================================== */

    /** Partition variants into seats / footrests by title. Disables bundle mode on failure. */
    _initBundle() {
      const seats = [];
      const footrests = { left: null, right: null };
      (this.config.variants || []).forEach(v => {
        const t = (v.title || '').toLowerCase();
        if (t.indexOf('seat') !== -1) {
          seats.push(v);
        } else if (t.indexOf('footrest') !== -1) {
          if (t.indexOf('left') !== -1) footrests.left = v;
          else if (t.indexOf('right') !== -1) footrests.right = v;
        }
      });

      if (seats.length === 0 || (!footrests.left && !footrests.right)) {
        console.warn('[Wuva] Bundle mode: could not partition seats/footrests from variant titles; falling back to the standard widget.');
        this.config.bundleMode = false;
        return;
      }

      this.bundle = { seats, footrests };
      this.state.footrest = { left: false, right: false };

      // The main variant must be a seat. If the page landed on a footrest variant
      // (e.g. ?variant=), switch to the first available seat instead.
      if (!this.variant || !seats.some(s => s.id == this.variant.id)) {
        this.variant = seats.find(s => s.available) || seats[0];
      }
    }

    _seatLabel(title) {
      return title;
    }

    _renderBundlePickers() {
      const wrap = el('div', { className: 'totalcommerce-bundle-pickers' });

      // Seat size (required)
      const seatGroup = el('div', { className: 'totalcommerce-bundle-group' });
      seatGroup.appendChild(el('div', { className: 'totalcommerce-bundle-label', textContent: 'Seat size' }));
      const seatOpts = el('div', { className: 'totalcommerce-bundle-options' });
      this.bundle.seats.forEach(v => {
        const btn = el('button', { type: 'button',
          className: 'totalcommerce-bundle-btn', role: 'radio',
          'data-seat-id': String(v.id),
          'aria-checked': this.variant && this.variant.id == v.id ? 'true' : 'false',
        });
        btn.appendChild(el('span', { className: 'totalcommerce-bundle-btn-title', textContent: this._seatLabel(v.title) }));
        if (v.available === false) { btn.disabled = true; btn.classList.add('is-unavailable'); }
        btn.addEventListener('click', () => this._selectSeat(v.id));
        seatOpts.appendChild(btn);
      });
      seatGroup.appendChild(seatOpts);
      this.refs.seatOptions = seatOpts;
      wrap.appendChild(seatGroup);

      // Elevating footrest (optional — none / left / right / both)
      const frGroup = el('div', { className: 'totalcommerce-bundle-group' });
      frGroup.appendChild(el('div', { className: 'totalcommerce-bundle-label', textContent: 'Elevating footrest (optional)' }));
      const frOpts = el('div', { className: 'totalcommerce-bundle-options' });
      ['left', 'right'].forEach(side => {
        const v = this.bundle.footrests[side];
        if (!v) return;
        const btn = el('button', { type: 'button',
          className: 'totalcommerce-bundle-btn totalcommerce-bundle-btn--toggle', role: 'checkbox',
          'data-footrest-side': side,
          'aria-checked': this.state.footrest[side] ? 'true' : 'false',
        });
        btn.appendChild(el('span', { className: 'totalcommerce-bundle-btn-title', textContent: side === 'left' ? 'Left' : 'Right' }));
        if (v.available === false) { btn.disabled = true; btn.classList.add('is-unavailable'); }
        btn.addEventListener('click', () => this._toggleFootrest(side));
        frOpts.appendChild(btn);
      });
      frGroup.appendChild(frOpts);
      this.refs.footrestOptions = frOpts;
      wrap.appendChild(frGroup);

      return wrap;
    }

    /** Seat selection — swaps the main variant and runs the usual variant reconcile. */
    _selectSeat(id) {
      const v = this._findVariant(id);
      if (!v) return;
      this.variant = v;

      if (this.refs.seatOptions) {
        this.refs.seatOptions.querySelectorAll('[data-seat-id]').forEach(b => {
          b.setAttribute('aria-checked', b.dataset.seatId == id ? 'true' : 'false');
        });
      }

      // Keep the theme form's hidden id in sync (fallbacks / analytics read it).
      const form = this._getProductForm() && this._getProductForm().querySelector('form');
      const idInput = form && form.querySelector('input[name="id"]');
      if (idInput) idInput.value = String(v.id);

      this._reconcileVariantChange();
      this._updateBundleBreakdown();
    }

    /** Footrest toggle — None/Left/Right/Both. Updates companion lines + breakdown. */
    _toggleFootrest(side) {
      const fv = this.bundle.footrests[side];
      if (!fv || fv.available === false) return;

      this.state.footrest[side] = !this.state.footrest[side];

      if (this.refs.footrestOptions) {
        this.refs.footrestOptions.querySelectorAll('[data-footrest-side]').forEach(b => {
          b.setAttribute('aria-checked', this.state.footrest[b.dataset.footrestSide] ? 'true' : 'false');
        });
      }

      if (this.state.currentTab === 'rental') this._ensureFootrestRentalPricing();
      this._updateBundleBreakdown();
    }

    /** The footrest variant objects currently selected (0–2). */
    _bundleSelectedFootrestVariants() {
      return ['left', 'right']
        .filter(s => this.state.footrest && this.state.footrest[s] && this.bundle.footrests[s])
        .map(s => this.bundle.footrests[s]);
    }

    /* ── Variant-agnostic VAT-twin helpers (parallel the this.variant ones) ── */
    _variantPairId(v) { return this._parsePairId(v && v.vatReliefPairId); }
    _variantHasPair(v) { return !!this._variantPairId(v); }
    _variantBaseIsExVat(v) { return v && v.taxable === false; }
    _variantExVatId(v) { return this._variantBaseIsExVat(v) ? v.id : this._variantPairId(v); }
    _variantIncVatId(v) { return this._variantBaseIsExVat(v) ? this._variantPairId(v) : v.id; }

    _adjustedPriceForVariant(cents, v) {
      const baseEx = this._variantBaseIsExVat(v);
      return this.state.vatRelief ? exVatPrice(cents, baseEx) : incVatPrice(cents, baseEx);
    }

    /** The term name ("3 Months"…) of the currently selected seat plan, or null. */
    _selectedTermName() {
      const plan = this._variantPlans().find(p => p.id == this.state.selectedPlanId);
      return plan ? plan.name : null;
    }

    /** A variant's own plan id for a given term name, intersected with its allocations. */
    _variantPlanForTermName(v, termName) {
      const target = String(termName || '').trim().toLowerCase();
      if (!target) return null;
      const allocated = new Set((v.sellingPlanAllocations || []).map(a => a.id));
      let found = null;
      (this.config.sellingPlanGroups || []).forEach(g => {
        (g.plans || []).forEach(p => {
          if (found == null && allocated.has(p.id) && String(p.name || '').trim().toLowerCase() === target) {
            found = p.id;
          }
        });
      });
      return found;
    }

    /** A footrest's plan price (cents, pre-VAT-direction) for a term, or null. */
    _footrestTermPrice(v, termName) {
      const planId = this._variantPlanForTermName(v, termName);
      if (!planId) return null;
      const alloc = (v.sellingPlanAllocations || []).find(a => a.id == planId);
      return alloc ? alloc.perDeliveryPrice : null;
    }

    _buildBundleLineProperties(isRental) {
      const props = {};
      if (isRental) {
        props._revenueModel = 'calendar-rental';
      } else {
        props._revenue_model = this._getRevenueModel();
      }
      if (this.config.clientId) props._client_id = this.config.clientId;
      props._merchant = MERCHANT_NAME;

      const f = window.wuvaFulfilment;
      if (f && f.locationId) {
        props._fulfilment_location_id = f.locationId;
        props.Fulfilment = 'Collection';
        props['Collection store'] = f.store;
        if (f.address) props['Collection address'] = f.address;
      }
      return props;
    }

    /**
     * Build one Buy/Lease cart line for a variant, applying the VAT-twin swap and
     * (Lease) resolving the variant's own plan for the term, remapped to the twin
     * product's plan when swapping. Returns null if a required plan can't resolve.
     */
    async _buildBundleLine(v, termName, reliefActive) {
      const hasPair = this._variantHasPair(v);
      const targetId = hasPair
        ? (reliefActive ? this._variantExVatId(v) : this._variantIncVatId(v))
        : v.id;

      const line = {
        id: extractNumericId(targetId),
        quantity: 1,
        properties: this._buildBundleLineProperties(false),
      };

      if (termName) {
        const ownPlanId = this._variantPlanForTermName(v, termName);
        if (!ownPlanId) return null;
        let planId = ownPlanId;

        const pairId = this._variantPairId(v);
        if (hasPair && pairId && String(targetId) === String(pairId)) {
          const pairPlans = await this._loadPairPlans(pairId);
          const mapped = pairPlans ? this._matchPairPlan(ownPlanId, pairPlans) : null;
          if (!mapped) return null;
          planId = mapped;
        }
        line.selling_plan = extractNumericId(planId);
      }

      return line;
    }

    /** Buy / Lease add-to-cart: seat line + 0–2 footrest lines in one call. */
    async _bundleAddToCart() {
      if (this._bundleAdding) return;
      this._bundleAdding = true;
      try {
        await this._bundleAddToCartInner();
      } finally {
        this._bundleAdding = false;
      }
    }

    async _bundleAddToCartInner() {
      const isLease = this.state.currentTab === 'subscription' && !!this.state.selectedPlanId;
      const termName = isLease ? this._selectedTermName() : null;
      const reliefActive = this.state.vatRelief && this.state.vatFormValid;

      const items = [];
      const seatLine = await this._buildBundleLine(this.variant, termName, reliefActive);
      if (!seatLine) {
        this._showError('Sorry, this option is temporarily unavailable. Please try again.');
        return;
      }
      items.push(seatLine);

      for (const fv of this._bundleSelectedFootrestVariants()) {
        const fLine = await this._buildBundleLine(fv, termName, reliefActive);
        if (!fLine) {
          this._showError('Sorry, the selected footrest is temporarily unavailable. Please try again.');
          return;
        }
        items.push(fLine);
      }

      await this._submitCartItems(items, isLease ? 'lease-bundle' : 'buy-bundle');
    }

    /**
     * POST the lines to /cart/add.js and refresh the cart UI the way the theme's
     * own product form does (CartAddEvent → drawer open + section re-render).
     * Returns true on success.
     */
    async _submitCartItems(items, source) {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map(c => c.dataset.sectionId)
        .filter(Boolean);

      const body = { items };
      if (sectionIds.length) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }

      const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

      let data;
      try {
        const resp = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        data = await resp.json();
        if (!resp.ok || data.status) {
          this._showError((data && (data.description || data.message)) || 'Could not add to your cart. Please try again.');
          return false;
        }
      } catch (e) {
        console.error('[Wuva] Bundle add to cart failed:', e);
        this._showError('Could not add to your cart. Please try again.');
        return false;
      }

      const evtName = (window.ThemeEvents && window.ThemeEvents.cartUpdate) || 'cart:update';
      const form = this._getProductForm();
      const target = form || document;
      target.dispatchEvent(new CustomEvent(evtName, {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: (form && form.id) || '',
          data: {
            source: source,
            itemCount: items.length,
            productId: this.config.productId,
            sections: data.sections,
          },
        },
      }));
      return true;
    }

    /** Provision one calendar-rental plan for a variant/date range. Throws on failure. */
    async _provisionRentalPlan(v, start, end, taxable) {
      const key = rentalDatesKey(start, end, taxable);
      // Reuse the seat's prefetched plan when it matches.
      if (v.id === this.variant.id && this.state.preparedPlan && this.state.preparedPlan.key === key) {
        return {
          planId: this.state.preparedPlan.planId,
          internalId: this.state.preparedPlan.sellingPlanInternalId,
          fromCache: true,
        };
      }
      const url = `${TOTALCOMMERCE_API_BASE}/selling-plan/${encodeURIComponent(this.config.clientId)}/calendar-rental?api-version=1`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productVariantId: v.wuvaVariantId,
          startDate: fmtIsoDate(start),
          endDate: fmtIsoDate(end),
          taxable: taxable,
        }),
      });
      const data = await resp.json();
      if (!data || !data.isSuccess || !data.value || !data.value.sellingPlanInternalId) {
        throw new Error('Rental plan provisioning failed');
      }
      return {
        planId: extractNumericId(data.value.sellingPlanInternalId),
        internalId: data.value.sellingPlanInternalId,
        fromCache: false,
      };
    }

    /** Rent add-to-cart: provision a plan per line for the same dates, then add all. */
    async _bundleRentalAddToCart() {
      if (this._isCheckoutBlocked()) return;
      if (await this._refuseIfCartStoreConflicts()) return;

      const { start, end } = this.state.rentalDates;
      if (!start || !end || !this.variant || !this.variant.wuvaVariantId || !this.config.clientId) return;

      const btn = this.refs.rentalAtcBtn;
      const origLabel = btn ? btn.textContent : '';

      if (this._syncDebounceTimer) {
        clearTimeout(this._syncDebounceTimer);
        this._syncDebounceTimer = null;
        if (this.state.vatRelief && this.state.vatFormValid) {
          this._cartAttrPromise = this._setCartAttributes();
        }
      }
      await this._cartAttrPromise;

      const taxable = this._rentalIsTaxable();
      const variants = [this.variant].concat(this._bundleSelectedFootrestVariants());

      if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

      const settled = await Promise.allSettled(
        variants.map(v => this._provisionRentalPlan(v, start, end, taxable).then(r => ({ variant: v, ...r })))
      );

      const ok = settled.filter(s => s.status === 'fulfilled').map(s => s.value);
      const failed = settled.some(s => s.status === 'rejected');

      const abandonFresh = () => ok.forEach(p => {
        if (!p.fromCache) this._abandonRentalPlan({ sellingPlanInternalId: p.internalId });
      });

      if (failed) {
        abandonFresh();
        this._showError("Couldn’t set up your rental. Please try again.");
        if (btn) { btn.disabled = false; btn.textContent = origLabel; }
        return;
      }

      const items = ok.map(p => {
        const lineVariantId = this._variantHasPair(p.variant)
          ? (taxable === false ? this._variantExVatId(p.variant) : this._variantIncVatId(p.variant))
          : p.variant.id;
        const props = this._buildBundleLineProperties(true);
        props._startDate = fmtIsoDate(start);
        props._endDate = fmtIsoDate(end);
        return {
          id: extractNumericId(lineVariantId),
          quantity: 1,
          selling_plan: extractNumericId(p.planId),
          properties: props,
        };
      });

      const added = await this._submitCartItems(items, 'rental-bundle');
      if (!added) {
        abandonFresh();
        if (btn) { btn.disabled = false; btn.textContent = origLabel; }
        return;
      }

      // Plans are now consumed into the cart; clear the prefetch and reset dates so
      // a subsequent rental starts clean (we stay on the page, unlike the single-line
      // flow which navigates away).
      this.state.preparedPlan = null;
      if (this.state.calendarInstance) this.state.calendarInstance.clear();
      this._resetRentalInputs();
      if (btn) { btn.disabled = false; btn.textContent = origLabel; }
    }

    /** Lazily fetch calendar-rental pricing for the selected footrests (for the breakdown). */
    _ensureFootrestRentalPricing() {
      this._bundleSelectedFootrestVariants().forEach(fv => {
        if (fv._rentalPricing || fv._rentalPricingPending) return;
        fv._rentalPricingPending = true;
        this._fetchVariantRentalPricing(fv)
          .then(rp => {
            fv._rentalPricingPending = false;
            if (rp) { fv._rentalPricing = rp; this._updateBundleBreakdown(); }
          })
          .catch(() => { fv._rentalPricingPending = false; });
      });
    }

    _fetchVariantRentalPricing(v) {
      const clientId = (this.config.clientId || '').toUpperCase();
      const extId = v.externalVariantId;
      if (!clientId || !extId) return Promise.resolve(null);
      const url = `${TOTALCOMMERCE_API_BASE}/product-variant/${clientId}/?externalVariantId=${extId}&api-version=1`;
      return fetch(url, { headers: { 'Content-Type': 'application/json' } })
        .then(r => r.json())
        .then(data => (data && data.isSuccess && data.value)
          ? { dayRate: data.value.dayRate || 0, fixedCosts: data.value.fixedCosts || 0 }
          : null);
    }

    /** Render the footrest add-on lines + combined total above the buy buttons. */
    _updateBundleBreakdown() {
      const box = this.refs.addonBreakdown;
      if (!box || !this.bundle) return;

      const footrests = this._bundleSelectedFootrestVariants();
      if (footrests.length === 0) {
        box.style.display = 'none';
        box.innerHTML = '';
        return;
      }

      const tab = this.state.currentTab;
      const termName = tab === 'subscription' ? this._selectedTermName() : null;
      const { start, end } = this.state.rentalDates;
      const days = (start && end) ? Math.ceil((end - start) / 86400000) + 1 : 0;

      // Running total. Buy/Lease work in cents; Rent works in pounds (legacy basis).
      let totalCents = 0;
      let totalPounds = 0;
      let haveTotal = true;

      if (tab === 'purchase') {
        totalCents += this._adjustedPriceForVariant(this.variant.price, this.variant);
      } else if (tab === 'subscription') {
        const pp = this.planPrices[this.state.selectedPlanId];
        if (pp !== undefined) totalCents += this._adjustedPrice(pp); else haveTotal = false;
      } else if (tab === 'rental') {
        if (this.state.rentalPricing && days > 0) {
          totalPounds += this._adjustedPricePounds(this.state.rentalPricing.fixedCosts + this.state.rentalPricing.dayRate * days);
        } else haveTotal = false;
      }

      const rows = [];
      footrests.forEach(fv => {
        const label = (fv === this.bundle.footrests.left) ? 'Left footrest' : 'Right footrest';
        let priceLabel = '';
        if (tab === 'purchase') {
          const c = this._adjustedPriceForVariant(fv.price, fv);
          totalCents += c;
          priceLabel = this._fmt(c);
        } else if (tab === 'subscription') {
          const fc = this._footrestTermPrice(fv, termName);
          if (fc != null) { const adj = this._adjustedPriceForVariant(fc, fv); totalCents += adj; priceLabel = this._fmt(adj) + '/mo'; }
          else { haveTotal = false; }
        } else if (tab === 'rental') {
          const rp = fv._rentalPricing;
          if (rp && days > 0) { const p = this._adjustedPricePounds(rp.fixedCosts + rp.dayRate * days); totalPounds += p; priceLabel = `£${p.toFixed(2)}`; }
          else { haveTotal = false; }
        }
        rows.push({ label, priceLabel });
      });

      box.innerHTML = '';
      rows.forEach(r => {
        box.appendChild(el('div', { className: 'totalcommerce-addon-row' }, [
          el('span', { textContent: `+ ${r.label}` }),
          el('span', { textContent: r.priceLabel || '—' }),
        ]));
      });
      if (haveTotal) {
        const suffix = tab === 'subscription' ? '/mo' : '';
        const totalLabel = tab === 'rental' ? `£${totalPounds.toFixed(2)}` : (this._fmt(totalCents) + suffix);
        box.appendChild(el('div', { className: 'totalcommerce-addon-total' }, [
          el('span', { textContent: 'Total' }),
          el('span', { textContent: totalLabel }),
        ]));

        // Keep the Rent button's total in step with the combined figure.
        if (tab === 'rental' && this.refs.rentalAtcBtn && !this._isCheckoutBlocked() && days > 0) {
          this.refs.rentalAtcBtn.textContent = `Rent for £${totalPounds.toFixed(2)}`;
        }
      }
      box.style.display = '';
    }

    /**
     * V3.3: Override the theme's handleSubmit for VAT relief.
     */
    _interceptFormSubmit() {
      const formComponent = this._getProductForm();
      if (!formComponent) return;
      const form = formComponent.querySelector('form');
      if (!form || form.dataset.totalcommerceIntercepted) return;

      form.dataset.totalcommerceIntercepted = 'true';

      const origHandleSubmit = formComponent.handleSubmit.bind(formComponent);

      formComponent.handleSubmit = async (event) => {
        event.preventDefault();

        if (this._isCheckoutBlocked()) {
          this.state.vatFormTouched = { name: true, condition: true };
          this._validateVatField('name');
          this._validateVatField('condition');
          if (this.refs.vatSection) {
            this.refs.vatSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }

        if (await this._refuseIfCartStoreConflicts()) return;

        // Flush any pending debounced cart attribute write before ATC
        if (this._syncDebounceTimer) {
          clearTimeout(this._syncDebounceTimer);
          this._syncDebounceTimer = null;
          if (this.state.vatRelief && this.state.vatFormValid) {
            this._cartAttrPromise = this._setCartAttributes();
          }
        }
        await this._cartAttrPromise;

        // Bundle mode: the theme form only expresses one line. Add the seat +
        // footrest line(s) ourselves and skip the single-line submit entirely.
        if (this.config.bundleMode) {
          await this._bundleAddToCart();
          return;
        }

        // V4: resolve the correct variant. EX-VAT only when relief is claimed
        // AND the declaration is complete; otherwise the INC-VAT (taxable) pair.
        const variantInput = form.querySelector('input[name="id"]');
        const originalId = variantInput?.value;
        const planInput = form.querySelector('input[name="selling_plan"]');
        const originalPlan = planInput ? planInput.value : undefined;

        if (variantInput && this._hasVatPair()) {
          const reliefActive = this.state.vatRelief && this.state.vatFormValid;
          const targetId = reliefActive ? this._exVatVariantId() : this._incVatVariantId();
          const pairVariantId = this._pairVariantId();
          const swappingToPair = targetId && pairVariantId && String(targetId) === String(pairVariantId);

          // V3.7: swapping to the pair means a DIFFERENT product, so the displayed
          // product's selling_plan id is invalid there. Remap it to the pair
          // product's corresponding plan; block the add if none resolves.
          if (swappingToPair && planInput && planInput.value) {
            const pairPlans = await this._loadPairPlans(pairVariantId);
            const pairPlanId = pairPlans ? this._matchPairPlan(planInput.value, pairPlans) : null;
            if (!pairPlanId) {
              this._showError('Sorry, this plan is temporarily unavailable. Please try again.');
              return;
            }
            planInput.value = String(pairPlanId);
          }

          if (targetId) variantInput.value = String(targetId);
        }

        origHandleSubmit(event);

        if (variantInput) {
          setTimeout(() => {
            if (originalId) variantInput.value = originalId;
            if (planInput && originalPlan !== undefined) planInput.value = originalPlan;
          }, 200);
        }
      };
    }

    /* ── Tab Navigation (underline) ── */

    _renderTabs() {
      const nav = el('div', { className: 'totalcommerce-tabs', role: 'tablist', 'data-count': '0' });
      this.refs.tabNav = nav;

      // Subscribe/Lease tab
      if (this.config.hasSellingPlans) {
        const subBtn = el('button', { type: 'button',
          className: 'totalcommerce-tab-btn', role: 'tab',
          'aria-selected': 'false', 'data-tab': 'subscription',
          textContent: this.config.subscribeLabel || 'Subscribe',
        });
        subBtn.addEventListener('click', () => this._switchTab('subscription'));
        nav.appendChild(subBtn);
        this.refs.subTabBtn = subBtn;
      }

      // Rent tab (hidden until API confirms)
      const rentBtn = el('button', { type: 'button',
        className: 'totalcommerce-tab-btn', role: 'tab',
        'aria-selected': 'false', 'data-tab': 'rental',
        textContent: 'Rent',
        style: { display: 'none' },
      });
      rentBtn.addEventListener('click', () => this._switchTab('rental'));
      nav.appendChild(rentBtn);
      this.refs.rentTabBtn = rentBtn;

      // Buy tab
      if (this.config.allowsOneTimePurchase) {
        const buyBtn = el('button', { type: 'button',
          className: 'totalcommerce-tab-btn', role: 'tab',
          'aria-selected': 'false', 'data-tab': 'purchase',
          textContent: 'Buy',
        });
        buyBtn.addEventListener('click', () => this._switchTab('purchase'));
        nav.appendChild(buyBtn);
        this.refs.buyTabBtn = buyBtn;
      }

      this._updateTabCount();
      return nav;
    }

    /* ── Buy Panel ── */

    _renderBuyPanel() {
      const panel = el('div', { className: 'totalcommerce-panel', 'data-panel': 'purchase', 'data-active': 'false', role: 'tabpanel' });

      const priceWrap = el('div', { className: 'totalcommerce-buy-price' });
      const priceVal = el('div', { className: 'totalcommerce-buy-price-value', textContent: this._fmt(this._adjustedPrice(this.variant?.price || 0)) });
      priceWrap.appendChild(priceVal);
      panel.appendChild(priceWrap);
      this.refs.buyPrice = priceVal;

      const benefits = el('div', { className: 'totalcommerce-benefits-inline' });
      [
        { ico: 'truck', text: 'Free delivery' },
        { ico: 'checkCircle', text: '2-year warranty' },
        { ico: 'box', text: 'Yours to keep' },
      ].forEach(b => {
        const item = el('div', { className: 'totalcommerce-benefit-item' });
        item.innerHTML = `${icon(b.ico)} ${b.text}`;
        benefits.appendChild(item);
      });
      panel.appendChild(benefits);

      if (this.config.hasSellingPlans) {
        const plans = this._variantPlans();
        let lowestPrice = Infinity;
        plans.forEach(p => {
          const pp = this.planPrices[p.id] || this.variant?.price || 0;
          if (pp < lowestPrice) lowestPrice = pp;
        });

        const nudge = el('div', { className: 'totalcommerce-nudge-card' });
        const nudgeLeft = el('div');
        const nudgeVerb = (this.config.subscribeLabel || 'Subscribe').toLowerCase();
        const nudgeText = el('div', { className: 'totalcommerce-nudge-text', textContent: `Or ${nudgeVerb} from ${this._fmt(this._adjustedPrice(lowestPrice))}/mo` });
        const nudgeSub = el('div', { className: 'totalcommerce-nudge-sub', textContent: 'Includes wear & tear cover, free delivery & returns' });
        nudgeLeft.append(nudgeText, nudgeSub);
        nudge.appendChild(nudgeLeft);

        const nudgeBtn = el('button', { type: 'button', className: 'totalcommerce-nudge-btn', textContent: 'View plans' });
        nudgeBtn.addEventListener('click', () => this._switchTab('subscription'));
        nudge.appendChild(nudgeBtn);

        if (plans.length === 0) nudge.style.display = 'none';
        panel.appendChild(nudge);
        this.refs.nudgeCard = nudge;
        this.refs.nudgeText = nudgeText;
      }

      this.refs.buyPanel = panel;
      return panel;
    }

    /* ── Subscribe Panel ── */

    _renderSubscriptionPanel() {
      const panel = el('div', { className: 'totalcommerce-panel', 'data-panel': 'subscription', 'data-active': 'false', role: 'tabpanel' });

      const grid = el('div', { className: 'totalcommerce-plans', 'data-count': '0' });
      panel.appendChild(grid);
      this.refs.planGrid = grid;

      const summary = el('div', { className: 'totalcommerce-breakdown' });
      const termRow = el('div', { className: 'totalcommerce-breakdown-row' }, [
        el('span', { textContent: 'Minimum term' }),
        el('span', { textContent: '—' }),
      ]);
      const priceRow = el('div', { className: 'totalcommerce-breakdown-row' }, [
        el('span', { textContent: 'Monthly price' }),
        el('span', { textContent: '—' }),
      ]);
      summary.append(termRow, priceRow);
      panel.appendChild(summary);

      const benefits = el('div', { className: 'totalcommerce-benefits-inline' });
      [
        { ico: 'truck', text: 'Free delivery & returns' },
        { ico: 'refresh', text: 'Cancel anytime after your minimum term' },
        { ico: 'shield', text: 'Wear & tear cover' },
      ].forEach(b => {
        const item = el('div', { className: 'totalcommerce-benefit-item' });
        item.innerHTML = `${icon(b.ico)} ${b.text}`;
        benefits.appendChild(item);
      });
      panel.appendChild(benefits);

      this.refs.summaryTerm = termRow.lastElementChild;
      this.refs.summaryPrice = priceRow.lastElementChild;
      this.refs.subscriptionPanel = panel;

      this._populatePlanGrid();

      return panel;
    }

    /**
     * (Re)build the plan grid buttons for the current variant. Preserves the
     * user's selection if the plan is still allocated to the new variant;
     * otherwise defaults to the last plan in the grid (matching initial render).
     */
    _populatePlanGrid() {
      const grid = this.refs.planGrid;
      if (!grid) return;

      const plans = this._variantPlans();
      const count = Math.min(plans.length, 4);
      grid.dataset.count = String(count);
      grid.innerHTML = '';

      let highestPrice = 0;
      plans.forEach(p => {
        const pp = this.planPrices[p.id] || 0;
        if (pp > highestPrice) highestPrice = pp;
      });

      plans.slice(0, 4).forEach((plan, i) => {
        const pp = this.planPrices[plan.id] || 0;
        const isLast = i === count - 1;

        const btn = el('button', { type: 'button',
          className: 'totalcommerce-plan-btn', role: 'radio',
          'aria-checked': isLast ? 'true' : 'false',
          'data-plan-id': String(plan.id),
        });

        if (isLast && count > 1) {
          btn.appendChild(el('span', { className: 'totalcommerce-plan-badge', textContent: 'Best value' }));
        }

        btn.appendChild(el('div', { className: 'totalcommerce-plan-duration', textContent: parsePlanDuration(plan.name) }));
        const priceDiv = el('div', { className: 'totalcommerce-plan-price' });
        priceDiv.innerHTML = `${this._fmt(this._adjustedPrice(pp))}<span class="totalcommerce-plan-per">/mo</span>`;
        btn.appendChild(priceDiv);

        let savingsText = 'Most flexible';
        if (pp !== highestPrice && highestPrice > 0) {
          const pct = Math.round(((highestPrice - pp) / highestPrice) * 100);
          savingsText = pct > 0 ? `Save ${pct}%` : 'Best value';
        }
        btn.appendChild(el('div', { className: 'totalcommerce-plan-savings', textContent: savingsText }));

        btn.addEventListener('click', () => this._selectPlan(plan.id));
        grid.appendChild(btn);
      });

      if (plans.length > 0) {
        const previousIsValid = this.state.selectedPlanId && plans.some(p => p.id == this.state.selectedPlanId);
        const targetId = previousIsValid
          ? this.state.selectedPlanId
          : plans[Math.min(plans.length - 1, 3)].id;
        this._selectPlan(targetId);
      } else {
        this.state.selectedPlanId = null;
        this._syncSellingPlanToForm();
      }
    }

    /* ── Rental Panel ── */

    _renderRentalPanel() {
      const panel = el('div', { className: 'totalcommerce-panel', 'data-panel': 'rental', 'data-active': 'false', role: 'tabpanel' });

      const dateRow = el('div', { className: 'totalcommerce-date-row' });

      const startGroup = el('div', { className: 'totalcommerce-date-group' });
      startGroup.appendChild(el('label', { className: 'totalcommerce-date-label', textContent: 'Delivery date' }));
      const startInput = el('input', { className: 'totalcommerce-date-input', type: 'text', placeholder: 'Select date', readonly: '' });
      startGroup.appendChild(startInput);

      const endGroup = el('div', { className: 'totalcommerce-date-group' });
      endGroup.appendChild(el('label', { className: 'totalcommerce-date-label', textContent: 'Collection date' }));
      const endInput = el('input', { className: 'totalcommerce-date-input', type: 'text', placeholder: 'Select date', readonly: '' });
      endGroup.appendChild(endInput);

      dateRow.append(startGroup, endGroup);
      panel.appendChild(dateRow);

      const bd = el('div', { className: 'totalcommerce-breakdown', style: { display: 'none' } });
      const periodRow = el('div', { className: 'totalcommerce-breakdown-row' }, [
        el('span', { textContent: 'Rental period' }),
        el('span', { textContent: '0 days' }),
      ]);
      const rateRow = el('div', { className: 'totalcommerce-breakdown-row' }, [
        el('span', { textContent: 'Daily rate' }),
        el('span', { textContent: '£0/day' }),
      ]);
      const totalRow = el('div', { className: 'totalcommerce-breakdown-total' }, [
        el('span', { textContent: 'Total' }),
        el('span', { textContent: '£0.00' }),
      ]);
      bd.append(periodRow, rateRow, totalRow);
      panel.appendChild(bd);

      const benefits = el('div', { className: 'totalcommerce-benefits-inline' });
      [
        { ico: 'truck', text: 'Free delivery' },
        { ico: 'refresh', text: 'Free collection' },
        { ico: 'shield', text: 'Insured' },
      ].forEach(b => {
        const item = el('div', { className: 'totalcommerce-benefit-item' });
        item.innerHTML = `${icon(b.ico)} ${b.text}`;
        benefits.appendChild(item);
      });
      panel.appendChild(benefits);

      const atcBtn = el('button', { type: 'button',
        className: 'totalcommerce-rental-atc',
        textContent: 'Select dates to rent',
        disabled: '',
        style: { display: 'none' },
      });
      atcBtn.addEventListener('click', () => this._handleRentalAddToCart());

      this.refs.startInput = startInput;
      this.refs.endInput = endInput;
      this.refs.rentalBreakdown = bd;
      this.refs.rentalPeriod = periodRow.lastElementChild;
      this.refs.dailyRate = rateRow.lastElementChild;
      this.refs.rentalTotal = totalRow.lastElementChild;
      this.refs.rentalAtcBtn = atcBtn;
      this.refs.rentalPanel = panel;

      const openCal = (e) => {
        e.preventDefault();
        if (this.state.calendarInstance) this.state.calendarInstance.open();
      };
      startInput.addEventListener('click', openCal);
      startInput.addEventListener('focus', openCal);
      endInput.addEventListener('click', openCal);
      endInput.addEventListener('focus', openCal);

      return panel;
    }

    /* ======================================================================
       TAB MANAGEMENT
       ====================================================================== */

    _switchTab(tab) {
      this.state.currentTab = tab;

      if (tab === 'rental') {
        document.body.classList.add('totalcommerce-rental-active');
      } else {
        document.body.classList.remove('totalcommerce-rental-active');
      }

      this.container.querySelectorAll('.totalcommerce-tab-btn').forEach(btn => {
        btn.setAttribute('aria-selected', btn.dataset.tab === tab ? 'true' : 'false');
      });

      this.container.querySelectorAll('.totalcommerce-panel').forEach(p => {
        p.dataset.active = p.dataset.panel === tab ? 'true' : 'false';
      });

      if (tab === 'rental' && !this.state.calendarLoaded) {
        this._loadCalendar();
      }

      if (this.refs.rentalAtcBtn) {
        this.refs.rentalAtcBtn.style.display = tab === 'rental' ? '' : 'none';
      }

      // V3.5: Sync selling plan + line item properties into form on tab change
      this._syncSellingPlanToForm();

      // V3.9: the active tab decides both whether the wallet button may show (hidden on rental)
      // and which companion lines an express purchase must carry (the mandate rides subscription
      // main lines only), so reconcile now that the tab has actually changed.
      this._reconcileAcceleratedCheckout();

      this._updateVatSaving();

      // V3.7: warm the pair-product plan cache when the Subscribe tab is active
      this._preparePairPlan();

      // Bundle: the add-on prices depend on the active tab; refresh the breakdown
      // (and warm footrest rental pricing when entering the Rent tab).
      if (this.config.bundleMode) {
        if (tab === 'rental') this._ensureFootrestRentalPricing();
        this._updateBundleBreakdown();
      }
    }

    _updateTabCount() {
      if (!this.refs.tabNav) return;
      const visible = Array.from(this.refs.tabNav.querySelectorAll('.totalcommerce-tab-btn'))
        .filter(b => b.style.display !== 'none');
      this.refs.tabNav.dataset.count = String(visible.length);
    }

    /**
     * Show or hide the Subscribe tab based on whether the current variant has
     * any allocated selling plans. If the subscription tab was active and the
     * new variant has no plans, fall back to purchase (or rental).
     */
    _updateSubscriptionTabVisibility() {
      if (!this.refs.subTabBtn) return;
      const hasPlans = this._variantPlans().length > 0;
      this.refs.subTabBtn.style.display = hasPlans ? '' : 'none';
      this._updateTabCount();
      if (!hasPlans && this.state.currentTab === 'subscription') {
        if (this.config.allowsOneTimePurchase) {
          this._switchTab('purchase');
        } else if (this.refs.rentTabBtn && this.refs.rentTabBtn.style.display !== 'none') {
          this._switchTab('rental');
        }
      }
    }

    _updateNudgeVisibility() {
      if (!this.refs.nudgeCard) return;
      this.refs.nudgeCard.style.display = this._variantPlans().length > 0 ? '' : 'none';
    }

    /* ======================================================================
       PLAN SELECTION
       ====================================================================== */

    _selectPlan(planId) {
      this.state.selectedPlanId = planId;

      this.container.querySelectorAll('.totalcommerce-plan-btn').forEach(btn => {
        btn.setAttribute('aria-checked', btn.dataset.planId == planId ? 'true' : 'false');
      });

      const pp = this.planPrices[planId];
      const plan = this._variantPlans().find(p => p.id == planId);

      if (this.refs.summaryTerm && plan) {
        this.refs.summaryTerm.textContent = parsePlanDuration(plan.name);
      }
      if (this.refs.summaryPrice && pp !== undefined) {
        this.refs.summaryPrice.textContent = `${this._fmt(this._adjustedPrice(pp))}/mo`;
      }

      // V3.5: Sync selling plan + line item properties into form on plan change
      this._syncSellingPlanToForm();

      // V3.9: a selected plan is what makes the subscription main line a Total Commerce
      // selling-plan line, so the mandate an express purchase must carry depends on it.
      this._reconcileAcceleratedCheckout();

      this._updateVatSaving();

      // V3.7: a different plan may map to a different pair plan — warm the cache
      this._preparePairPlan();

      // Bundle: the selected term changes the footrest add-on price.
      if (this.config.bundleMode) this._updateBundleBreakdown();
    }

    /* ======================================================================
       QUICK-PICK DURATION (Rental)
       ====================================================================== */

    _selectQuickDuration(days) {
      this.state.rentalQuickDays = days;

      if (this.refs.durationRow) {
        this.refs.durationRow.querySelectorAll('.totalcommerce-duration-btn').forEach(btn => {
          btn.setAttribute('aria-checked', btn.dataset.days == days ? 'true' : 'false');
        });
      }

      if (this.state.calendarInstance) {
        const minDate = this.state.rentalConstraints?.startDate
          ? new Date(this.state.rentalConstraints.startDate)
          : new Date();
        const start = new Date(minDate);
        if (start <= new Date()) {
          start.setDate(new Date().getDate() + 1);
        }
        const end = new Date(start);
        end.setDate(end.getDate() + days - 1);

        this.state.calendarInstance.setDate([start, end], true);
      } else {
        const start = new Date();
        start.setDate(start.getDate() + 1);
        const end = new Date(start);
        end.setDate(end.getDate() + days - 1);
        this.state.rentalDates = { start, end };
        this._prepareRentalPlan();

        const opts = { day: 'numeric', month: 'short' };
        if (this.refs.startInput) this.refs.startInput.value = start.toLocaleDateString('en-GB', opts);
        if (this.refs.endInput) this.refs.endInput.value = end.toLocaleDateString('en-GB', opts);

        this._calcRentalPricing();
        this._updateRentalButtonState();
      }
    }

    /* ======================================================================
       FORM INTEGRATION
       ====================================================================== */

    _getProductForm() {
      return document.querySelector(
        `product-form-component[data-section-id="${this.sectionId}"][data-product-id="${this.config.productId}"]`
      ) || document.querySelector(
        `product-form-component[data-product-id="${this.config.productId}"]`
      );
    }

    /* ======================================================================
       PRICE UPDATES
       ====================================================================== */

    _updateAllPrices() {
      if (this.refs.buyPrice) {
        this.refs.buyPrice.textContent = this._fmt(this._adjustedPrice(this.variant?.price || 0));
      }

      const plans = this._variantPlans();
      let lowestPrice = Infinity;
      let highestPrice = 0;
      plans.forEach(p => {
        const pp = this.planPrices[p.id] || 0;
        if (pp < lowestPrice) lowestPrice = pp;
        if (pp > highestPrice) highestPrice = pp;
      });

      if (this.refs.nudgeText && lowestPrice < Infinity) {
        const nv = (this.config.subscribeLabel || 'Subscribe').toLowerCase();
        this.refs.nudgeText.textContent = `Or ${nv} from ${this._fmt(this._adjustedPrice(lowestPrice))}/mo`;
      }

      this.container.querySelectorAll('.totalcommerce-plan-btn').forEach(btn => {
        const pid = btn.dataset.planId;
        const pp = this.planPrices[pid];
        if (pp === undefined) return;

        const priceEl = btn.querySelector('.totalcommerce-plan-price');
        if (priceEl) priceEl.innerHTML = `${this._fmt(this._adjustedPrice(pp))}<span class="totalcommerce-plan-per">/mo</span>`;

        const savingsEl = btn.querySelector('.totalcommerce-plan-savings');
        if (savingsEl) {
          if (pp === highestPrice) {
            savingsEl.textContent = 'Most flexible';
          } else if (highestPrice > 0) {
            const pct = Math.round(((highestPrice - pp) / highestPrice) * 100);
            savingsEl.textContent = pct > 0 ? `Save ${pct}%` : 'Best value';
          }
        }
      });

      if (this.refs.summaryPrice && this.state.selectedPlanId) {
        const pp = this.planPrices[this.state.selectedPlanId];
        if (pp !== undefined) {
          this.refs.summaryPrice.textContent = `${this._fmt(this._adjustedPrice(pp))}/mo`;
        }
      }

      this._updateVatSaving();
      this._calcRentalPricing();

      // Bundle: VAT toggle / variant change move the add-on prices too.
      if (this.config.bundleMode) this._updateBundleBreakdown();
    }

    /* ======================================================================
       RENTAL: API CALLS
       ====================================================================== */

    async _checkRentalAvailability() {
      // Source of truth: the `totalcommerce.availability` variant metafield.
      // If it's present and says rentals are off, skip the fetch entirely.
      // If it's missing (legacy variants not yet backfilled), fall through
      // to the legacy /product-variant fetch which infers from isCalendarRentingEnabled.
      const a = this.variant && this.variant.availability;
      if (a && a.calendarRentalsEnabled !== true) {
        this._hideRentTab();
        return;
      }

      const clientId = (this.config.clientId || '').toUpperCase();
      const extId = this.variant?.externalVariantId;
      if (!clientId || !extId) {
        this._hideRentTab();
        return;
      }

      this._abortCtrl.abort();
      this._abortCtrl = new AbortController();

      try {
        const url = `${TOTALCOMMERCE_API_BASE}/product-variant/${clientId}/?externalVariantId=${extId}&api-version=1`;
        const resp = await fetch(url, {
          signal: this._abortCtrl.signal,
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await resp.json();

        if (data.isSuccess && data.value?.isCalendarRentingEnabled) {
          this.state.rentalPricing = {
            dayRate: data.value.dayRate || 0,
            fixedCosts: data.value.fixedCosts || 0,
            lowestDailyCost: data.value.lowestDailyCost || 0,
            hireFeeInternalId: data.value.hireFeeInternalId,
            dayRateInternalId: data.value.dayRateInternalId,
            vatFreeHireFeeInternalId: data.value.vatFreeHireFeeInternalId || null,
            vatFreeDayRateInternalId: data.value.vatFreeDayRateInternalId || null,
          };
          this._showRentTab();

          if (data.value.id) {
            await this._fetchRentalConstraints(clientId, data.value.id);
          }
        } else {
          this._hideRentTab();
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('[Wuva] Rental check failed:', e);
          this._hideRentTab();
        }
      }
    }

    async _fetchRentalConstraints(clientId, productVariantId) {
      try {
        const url = `${TOTALCOMMERCE_API_BASE}/product-variant/minimum-calendar-rental-dates/?productVariantId=${productVariantId.toUpperCase()}&api-version=1`;
        const resp = await fetch(url, {
          signal: this._abortCtrl.signal,
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await resp.json();

        if (data.isSuccess && data.value) {
          this.state.rentalConstraints = data.value;
          this.state.minRentalDays = data.value.minimumRentalPeriodInDays;
          this._applyCalendarConstraints();
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('[Wuva] Rental constraints failed:', e);
        }
      }
    }

    _showRentTab() {
      if (this.refs.rentTabBtn) this.refs.rentTabBtn.style.display = '';
      this._updateTabCount();
    }

    _hideRentTab() {
      if (this.refs.rentTabBtn) this.refs.rentTabBtn.style.display = 'none';
      this.state.rentalPricing = null;
      this._updateTabCount();
      if (this.state.currentTab === 'rental') {
        if (this.config.allowsOneTimePurchase) this._switchTab('purchase');
        else if (this.config.hasSellingPlans) this._switchTab('subscription');
      }
    }

    /* ======================================================================
       RENTAL: CALENDAR
       Self-contained range picker rendered with CSS Grid so day cells
       always map 1:1 to weekday columns.
       ====================================================================== */

    _loadCalendar() {
      if (this.state.calendarLoaded) return;
      this.state.calendarLoaded = true;
      this._initCalendar();
    }

    _initCalendar() {
      if (!this.refs.startInput) return;

      const widget = this;
      const today = startOfDay(new Date());
      const initMin = this.state.rentalConstraints?.startDate
        ? startOfDay(new Date(this.state.rentalConstraints.startDate))
        : today;

      const cal = {
        viewYear: initMin.getFullYear(),
        viewMonth: initMin.getMonth(),
        startDate: null,
        endDate: null,
        minDate: initMin,
        hoverDate: null,
        isOpen: false,
      };

      const container = el('div', { className: 'totalcommerce-cal', 'data-open': 'false', role: 'dialog' });
      // Keep clicks inside the calendar from reaching the document-level
      // outside-click handler (re-rendering detaches the click target).
      container.addEventListener('mousedown', (e) => e.stopPropagation());
      container.addEventListener('click', (e) => e.stopPropagation());
      document.body.appendChild(container);
      cal.container = container;

      const sameDay = (a, b) => a && b && a.getTime() === b.getTime();

      const isNonOperatingDay = (cellDate) => {
        const c = widget.state.rentalConstraints;
        if (!c) return false;
        const day = cellDate.getDay();
        if (day === 6 && c.deliveryOperatingSaturday === false) return true;
        if (day === 0 && c.deliveryOperatingSunday === false) return true;
        return false;
      };

      const isDisabled = (cellDate) => {
        if (cellDate < cal.minDate) return true;
        if (isNonOperatingDay(cellDate)) return true;
        // Only block days that exceed the maximum span from the chosen start.
        // Days shorter than minRentalDays stay clickable; we clamp on click.
        if (cal.startDate && !cal.endDate) {
          const diff = daysBetween(cal.startDate, cellDate) + 1;
          if (diff > MAX_RENTAL_DAYS) return true;
        }
        return false;
      };

      const buildMonth = (month, year, isFirst, isLast) => {
        const wrap = el('div', { className: 'totalcommerce-cal-month' });

        const header = el('div', { className: 'totalcommerce-cal-header' });

        const prev = el('button', {
          type: 'button',
          className: 'totalcommerce-cal-nav',
          'aria-label': 'Previous month',
        });
        prev.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 12 6 8l4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        if (!isFirst) prev.style.visibility = 'hidden';
        prev.addEventListener('click', () => {
          cal.viewMonth -= 1;
          if (cal.viewMonth < 0) { cal.viewMonth = 11; cal.viewYear -= 1; }
          render();
        });
        header.appendChild(prev);

        header.appendChild(el('div', {
          className: 'totalcommerce-cal-title',
          textContent: `${CAL_MONTHS[month]} ${year}`,
        }));

        const next = el('button', {
          type: 'button',
          className: 'totalcommerce-cal-nav',
          'aria-label': 'Next month',
        });
        next.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        if (!isLast) next.style.visibility = 'hidden';
        next.addEventListener('click', () => {
          cal.viewMonth += 1;
          if (cal.viewMonth > 11) { cal.viewMonth = 0; cal.viewYear += 1; }
          render();
        });
        header.appendChild(next);

        wrap.appendChild(header);

        const wd = el('div', { className: 'totalcommerce-cal-weekdays' });
        CAL_WEEKDAYS.forEach(d => {
          wd.appendChild(el('div', { className: 'totalcommerce-cal-weekday', textContent: d }));
        });
        wrap.appendChild(wd);

        const grid = el('div', { className: 'totalcommerce-cal-days' });
        const firstWeekday = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        for (let i = 0; i < firstWeekday; i++) {
          grid.appendChild(el('div', { className: 'totalcommerce-cal-blank' }));
        }

        for (let d = 1; d <= daysInMonth; d++) {
          const cellDate = startOfDay(new Date(year, month, d));
          const classes = ['totalcommerce-cal-day'];
          const disabled = isDisabled(cellDate);

          if (disabled) classes.push('totalcommerce-cal-disabled');
          if (sameDay(cellDate, today)) classes.push('totalcommerce-cal-today');
          if (sameDay(cellDate, cal.startDate)) classes.push('totalcommerce-cal-start');
          if (sameDay(cellDate, cal.endDate)) classes.push('totalcommerce-cal-end');
          if (cal.startDate && cal.endDate && cellDate > cal.startDate && cellDate < cal.endDate) {
            classes.push('totalcommerce-cal-inrange');
          }
          if (cal.startDate && !cal.endDate && cal.hoverDate && cal.hoverDate > cal.startDate) {
            if (cellDate > cal.startDate && cellDate < cal.hoverDate) classes.push('totalcommerce-cal-inrange');
            if (sameDay(cellDate, cal.hoverDate) && !disabled) classes.push('totalcommerce-cal-hover-end');
          }

          const cell = el('button', {
            type: 'button',
            className: classes.join(' '),
            textContent: String(d),
          });
          if (disabled) cell.disabled = true;

          cell.addEventListener('click', () => {
            if (cell.disabled) return;
            if (!cal.startDate || (cal.startDate && cal.endDate)) {
              cal.startDate = cellDate;
              cal.endDate = null;
              widget.refs.startInput.value = fmtDateLabel(cellDate);
              widget.refs.endInput.value = 'Select date';
              widget.state.rentalDates = { start: cellDate, end: null };
              widget._prepareRentalPlan();
              widget._updateRentalButtonState();
              render();
              return;
            }
            if (cellDate <= cal.startDate) {
              cal.startDate = cellDate;
              cal.endDate = null;
              widget.refs.startInput.value = fmtDateLabel(cellDate);
              widget.refs.endInput.value = 'Select date';
              widget.state.rentalDates = { start: cellDate, end: null };
              widget._prepareRentalPlan();
              widget._updateRentalButtonState();
              render();
              return;
            }
            let endDate = cellDate;
            const rawDays = daysBetween(cal.startDate, endDate) + 1;
            if (rawDays > MAX_RENTAL_DAYS) {
              cal.startDate = null;
              cal.endDate = null;
              widget._resetRentalInputs();
              render();
              return;
            }
            if (widget.state.minRentalDays && rawDays < widget.state.minRentalDays) {
              endDate = new Date(cal.startDate);
              endDate.setDate(endDate.getDate() + widget.state.minRentalDays - 1);
            }
            while (isNonOperatingDay(endDate)) {
              endDate.setDate(endDate.getDate() + 1);
            }
            cal.endDate = endDate;
            widget.refs.startInput.value = fmtDateLabel(cal.startDate);
            widget.refs.endInput.value = fmtDateLabel(endDate);
            widget.state.rentalDates = { start: cal.startDate, end: endDate };
            widget._prepareRentalPlan();
            widget._calcRentalPricing();
            widget._updateRentalButtonState();
            render();
            setTimeout(() => cal.close(), 200);
          });

          cell.addEventListener('mouseenter', () => {
            if (!cal.startDate || cal.endDate || disabled) return;
            if (sameDay(cal.hoverDate, cellDate)) return;
            cal.hoverDate = cellDate;
            render();
          });

          grid.appendChild(cell);
        }

        wrap.appendChild(grid);
        return wrap;
      };

      const render = () => {
        const isMobile = window.innerWidth <= 640;
        const monthsToShow = isMobile ? 1 : 2;
        container.innerHTML = '';
        const monthsWrap = el('div', { className: 'totalcommerce-cal-months' });
        for (let i = 0; i < monthsToShow; i++) {
          const m = (cal.viewMonth + i) % 12;
          const y = cal.viewYear + Math.floor((cal.viewMonth + i) / 12);
          monthsWrap.appendChild(buildMonth(m, y, i === 0, i === monthsToShow - 1));
        }
        container.appendChild(monthsWrap);
      };

      const reposition = () => {
        const rect = widget.refs.startInput.getBoundingClientRect();
        const isMobile = window.innerWidth <= 640;
        const top = rect.bottom + window.scrollY + 8;
        if (isMobile) {
          container.style.left = '8px';
          container.style.right = '8px';
          container.style.width = 'auto';
          container.style.top = `${top}px`;
        } else {
          container.style.left = `${rect.left + window.scrollX}px`;
          container.style.right = '';
          container.style.width = '';
          container.style.top = `${top}px`;
        }
      };

      const onOutside = (e) => {
        if (container.contains(e.target)) return;
        if (e.target === widget.refs.startInput || e.target === widget.refs.endInput) return;
        cal.close();
      };

      cal.open = () => {
        if (cal.isOpen) return;
        cal.isOpen = true;
        container.dataset.open = 'true';
        if (cal.startDate) {
          cal.viewYear = cal.startDate.getFullYear();
          cal.viewMonth = cal.startDate.getMonth();
        }
        render();
        reposition();
        setTimeout(() => document.addEventListener('click', onOutside), 0);
      };

      cal.close = () => {
        if (!cal.isOpen) return;
        cal.isOpen = false;
        container.dataset.open = 'false';
        cal.hoverDate = null;
        document.removeEventListener('click', onOutside);
      };

      cal.clear = () => {
        cal.startDate = null;
        cal.endDate = null;
        cal.hoverDate = null;
        if (cal.isOpen) render();
      };

      cal.set = (key, value) => {
        if (key === 'minDate') {
          cal.minDate = startOfDay(value instanceof Date ? value : new Date(value));
          const viewFirst = new Date(cal.viewYear, cal.viewMonth, 1);
          const minMonthFirst = new Date(cal.minDate.getFullYear(), cal.minDate.getMonth(), 1);
          if (viewFirst < minMonthFirst) {
            cal.viewYear = cal.minDate.getFullYear();
            cal.viewMonth = cal.minDate.getMonth();
          }
          if (cal.isOpen) render();
        }
      };

      cal.setDate = (dates, triggerChange) => {
        if (!dates || dates.length === 0) {
          cal.startDate = null;
          cal.endDate = null;
        } else {
          cal.startDate = startOfDay(dates[0] instanceof Date ? dates[0] : new Date(dates[0]));
          cal.endDate = dates.length >= 2
            ? startOfDay(dates[1] instanceof Date ? dates[1] : new Date(dates[1]))
            : null;
          cal.viewYear = cal.startDate.getFullYear();
          cal.viewMonth = cal.startDate.getMonth();
        }
        if (cal.isOpen) render();
        if (triggerChange && cal.startDate && cal.endDate) {
          widget.refs.startInput.value = fmtDateLabel(cal.startDate);
          widget.refs.endInput.value = fmtDateLabel(cal.endDate);
          widget.state.rentalDates = { start: cal.startDate, end: cal.endDate };
          widget._prepareRentalPlan();
          widget._calcRentalPricing();
          widget._updateRentalButtonState();
        }
      };

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (cal.isOpen) {
            render();
            reposition();
          }
        }, 200);
      });

      this.state.calendarInstance = cal;
      this._resetRentalInputs();

      if (this.state.rentalQuickDays > 0) {
        this._selectQuickDuration(this.state.rentalQuickDays);
      }
    }

    _applyCalendarConstraints() {
      const cal = this.state.calendarInstance;
      if (!cal || !this.state.rentalConstraints) return;
      cal.set('minDate', new Date(this.state.rentalConstraints.startDate));
    }

    _resetRentalInputs() {
      if (this.refs.startInput) this.refs.startInput.value = '';
      if (this.refs.endInput) this.refs.endInput.value = '';
      this.refs.startInput?.setAttribute('placeholder', 'Select date');
      this.refs.endInput?.setAttribute('placeholder', 'Select date');
      this.state.rentalDates = { start: null, end: null };
      this._prepareRentalPlan();
      if (this.refs.rentalBreakdown) this.refs.rentalBreakdown.style.display = 'none';
      this._updateRentalButtonState();
    }

    _calcRentalPricing() {
      const { start, end } = this.state.rentalDates;
      if (!start || !end || !this.state.rentalPricing) return;

      const days = Math.ceil((end - start) / 86400000) + 1;
      if (days <= 0) return;

      const rp = this.state.rentalPricing;
      const total = rp.fixedCosts + (rp.dayRate * days);
      const effectiveDailyRate = total / days;

      if (this.refs.rentalBreakdown) this.refs.rentalBreakdown.style.display = '';
      if (this.refs.rentalPeriod) this.refs.rentalPeriod.textContent = `${days} day${days !== 1 ? 's' : ''}`;
      if (this.refs.dailyRate) this.refs.dailyRate.textContent = `${this._fmtPounds(effectiveDailyRate)}/day`;
      if (this.refs.rentalTotal) this.refs.rentalTotal.textContent = this._fmtPounds(total);

      if (this.refs.rentalAtcBtn && !this._isCheckoutBlocked()) {
        this.refs.rentalAtcBtn.textContent = `Rent for ${this._fmtPounds(total)}`;
      }

      this._updateVatSaving();

      // Bundle: the rental breakdown total includes the footrest(s) for these dates.
      if (this.config.bundleMode) this._updateBundleBreakdown();
    }

    _updateRentalButtonState() {
      if (!this.refs.rentalAtcBtn) return;
      const { start, end } = this.state.rentalDates;
      const hasDate = !!(start && end);
      const blocked = this._isCheckoutBlocked();

      this.refs.rentalAtcBtn.disabled = !hasDate || blocked;

      if (blocked) {
        this.refs.rentalAtcBtn.textContent = 'Fill in VAT declaration to rent';
      } else if (!hasDate) {
        this.refs.rentalAtcBtn.textContent = 'Select dates to rent';
      } else {
        this._calcRentalPricing();
      }
    }

    /* ======================================================================
       RENTAL: SELLING PLAN PREFETCH + ABANDON
       New flow (replacing the legacy two-variant hire fee / day rate ATC):
       on date pick we POST to /selling-plan/.../calendar-rental in the
       background, cache the plan, and on Rent click just navigate to
       /cart/add with the selling plan id. On date change the previously
       cached plan is sent through /calendar-rental/abandon so it doesn't
       linger in the merchant's group until the nightly cleanup sweep.
       ====================================================================== */

    _cancelInFlightRentalPrepare() {
      if (this.state.preparePlanAbortCtrl) {
        this.state.preparePlanAbortCtrl.abort();
        this.state.preparePlanAbortCtrl = null;
      }
      this.state.preparingPlanKey = null;
    }

    _prepareRentalPlan() {
      const self = this;
      const { start, end } = this.state.rentalDates;
      const taxable = this._rentalIsTaxable();
      const key = rentalDatesKey(start, end, taxable);

      // No valid range — clear pending state and abandon any cached plan
      // (date cleared, first click of new range, variant change).
      if (!key) {
        this._cancelInFlightRentalPrepare();
        if (this.state.preparedPlan) {
          this._abandonRentalPlan(this.state.preparedPlan);
        }
        this.state.preparedPlan = null;
        return;
      }

      // Required identifiers missing — silently skip; the Rent click handler
      // will surface a friendlier error if the user tries to proceed.
      if (!this.variant || !this.variant.wuvaVariantId || !this.config.clientId) {
        return;
      }

      // Cached plan for these dates is still valid — nothing to do.
      if (this.state.preparedPlan && this.state.preparedPlan.key === key) return;

      // Already preparing this key — let it complete.
      if (this.state.preparingPlanKey === key) return;

      // Different key in flight (or stale cached plan) — abort + abandon.
      this._cancelInFlightRentalPrepare();
      if (this.state.preparedPlan && this.state.preparedPlan.key !== key) {
        this._abandonRentalPlan(this.state.preparedPlan);
      }
      this.state.preparedPlan = null;

      this.state.preparingPlanKey = key;
      this.state.preparePlanAbortCtrl = new AbortController();
      const ctrl = this.state.preparePlanAbortCtrl;
      const capturedDates = { start, end };

      const url = `${TOTALCOMMERCE_API_BASE}/selling-plan/${encodeURIComponent(this.config.clientId)}/calendar-rental?api-version=1`;
      const body = {
        productVariantId: this.variant.wuvaVariantId,
        startDate: fmtIsoDate(start),
        endDate: fmtIsoDate(end),
        // false → backend builds the plan on the VAT-free variant at the ex-VAT
        // price; true → on the taxable variant at the full price.
        taxable: taxable,
      };

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      })
        .then(r => r.json())
        .then(data => {
          // Stale — dates changed while we were waiting. Abandon the plan
          // Shopify just created so it doesn't linger.
          if (self.state.preparingPlanKey !== key) {
            if (data && data.isSuccess && data.value && data.value.sellingPlanInternalId) {
              self._abandonRentalPlan({ sellingPlanInternalId: data.value.sellingPlanInternalId });
            }
            return;
          }
          self.state.preparingPlanKey = null;

          if (!data || !data.isSuccess || !data.value || !data.value.sellingPlanInternalId) {
            console.error('[Total Commerce] Rental prepare: unexpected response', data);
            return;
          }

          self.state.preparedPlan = {
            key,
            taxable,
            planId: extractNumericId(data.value.sellingPlanInternalId),
            sellingPlanInternalId: data.value.sellingPlanInternalId,
            startDate: capturedDates.start,
            endDate: capturedDates.end,
          };

          // User already clicked Rent and is waiting — finish navigation now.
          if (self.state.rentalRequestInFlight) {
            self._navigateToCart(self.state.preparedPlan);
          }
        })
        .catch(e => {
          if (e && e.name === 'AbortError') return;
          console.error('[Total Commerce] Rental prepare failed:', e);
          if (self.state.preparingPlanKey === key) {
            self.state.preparingPlanKey = null;
          }
          if (self.state.rentalRequestInFlight) {
            self.state.rentalRequestInFlight = false;
            self._showError("Couldn’t set up your rental. Please try again.");
            self._updateRentalButtonState();
          }
        });
    }

    _abandonRentalPlan(plan) {
      if (!plan || !plan.sellingPlanInternalId) return;
      if (!this.config.clientId) return;

      const url = `${TOTALCOMMERCE_API_BASE}/selling-plan/${encodeURIComponent(this.config.clientId)}/calendar-rental/abandon?api-version=1`;

      // keepalive so the request still completes if the user navigates away
      // (e.g. picks new dates and immediately clicks Rent — we still want the
      // abandon for the old dates to land).
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sellingPlanInternalId: plan.sellingPlanInternalId }),
        keepalive: true,
      }).catch(e => {
        console.warn('[Total Commerce] Abandon plan failed (relying on nightly cleanup):', e);
      });
    }

    _navigateToCart(preparedPlan) {
      // The backend built the plan against a specific Shopify variant keyed by
      // the `taxable` flag we sent (taxable → inc-VAT/taxable side, non-taxable
      // → ex-VAT/VAT-free side). The cart line MUST use that same variant or
      // Shopify rejects the selling plan. For VAT-relief pairs the displayed
      // page variant is the ex-VAT side and its taxable twin lives in the
      // vat_relief_pair metafield; _exVatVariantId/_incVatVariantId resolve both
      // regardless of which side is on the page.
      let variantId;
      if (this._hasVatPair()) {
        const targetId = preparedPlan.taxable === false
          ? this._exVatVariantId()
          : this._incVatVariantId();
        variantId = extractNumericId(targetId || this.variant.id);
      } else {
        variantId = extractNumericId(this.variant.id);
      }
      const params = new URLSearchParams();
      params.set('id', variantId);
      params.set('quantity', '1');
      params.set('selling_plan', preparedPlan.planId);
      params.append('properties[_startDate]', fmtIsoDate(preparedPlan.startDate));
      params.append('properties[_endDate]', fmtIsoDate(preparedPlan.endDate));
      params.append('properties[_revenueModel]', 'calendar-rental');
      if (this.config.clientId) {
        params.append('properties[_client_id]', this.config.clientId);
      }
      params.append('properties[_merchant]', MERCHANT_NAME);

      // Rental bypasses the product form entirely, so the picker's hidden inputs never apply. Carry
      // the same four properties here.
      var fulfilment = window.wuvaFulfilment;
      if (fulfilment && fulfilment.locationId) {
        params.append('properties[_fulfilment_location_id]', fulfilment.locationId);
        params.append('properties[Fulfilment]', 'Collection');
        params.append('properties[Collection store]', fulfilment.store);
        if (fulfilment.address) {
          params.append('properties[Collection address]', fulfilment.address);
        }
      }

      window.location.href = '/cart/add?' + params.toString();
    }

    /* ======================================================================
       RENTAL: ADD TO CART (thin layer over the prefetch cache)
       ====================================================================== */

    async _handleRentalAddToCart() {
      if (this.config.bundleMode) return this._bundleRentalAddToCart();
      if (this._isCheckoutBlocked()) return;
      if (await this._refuseIfCartStoreConflicts()) return;

      const { start, end } = this.state.rentalDates;
      if (!start || !end || !this.variant || !this.variant.wuvaVariantId || !this.config.clientId) return;

      const btn = this.refs.rentalAtcBtn;

      // VAT cart attributes still ride alongside the rental — flush any
      // pending debounced write before navigation so they're saved on the
      // cart before the page transition.
      if (this._syncDebounceTimer) {
        clearTimeout(this._syncDebounceTimer);
        this._syncDebounceTimer = null;
        if (this.state.vatRelief && this.state.vatFormValid) {
          this._cartAttrPromise = this._setCartAttributes();
        }
      }
      await this._cartAttrPromise;

      const key = rentalDatesKey(start, end, this._rentalIsTaxable());

      // Cache hit — happy path, instant navigation.
      if (this.state.preparedPlan && this.state.preparedPlan.key === key) {
        this._navigateToCart(this.state.preparedPlan);
        return;
      }

      // Prefetch in flight or needs kicking off — show feedback; the
      // .then inside _prepareRentalPlan will navigate when it lands.
      this.state.rentalRequestInFlight = true;
      btn.disabled = true;
      btn.textContent = 'Adding...';

      if (this.state.preparingPlanKey !== key) {
        this._prepareRentalPlan();
      }

      // Safety timeout — if nothing completes in 10s, release the spinner
      // and let the user retry.
      const self = this;
      setTimeout(() => {
        if (!self.state.rentalRequestInFlight) return;
        self.state.rentalRequestInFlight = false;
        self._showError('Rental request timed out. Please try again.');
        self._updateRentalButtonState();
      }, 10000);
    }

    _dispatchCartEvent() {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, data: { source: 'totalcommerce-widget' } },
          }));
          if (window.ThemeEvents?.cartUpdate) {
            document.dispatchEvent(new CustomEvent(window.ThemeEvents.cartUpdate, {
              bubbles: true,
              detail: { resource: cart, data: { source: 'totalcommerce-widget' } },
            }));
          }
        })
        .catch(() => {});
    }

    _showError(msg) {
      if (!this.refs.error) return;
      this.refs.error.textContent = msg;
      this.refs.error.dataset.visible = 'true';
      setTimeout(() => {
        if (this.refs.error) this.refs.error.dataset.visible = 'false';
      }, 5000);
    }

    /* ======================================================================
       EVENT BINDING (Horizon integration)
       ====================================================================== */

    _bindEvents() {
      const section = this.container.closest('.shopify-section');
      if (!section) return;

      section.addEventListener('variant:update', (e) => this._onVariantUpdate(e));
      section.addEventListener('variant:selected', (e) => this._onVariantSelected(e));

      if (window.ThemeEvents) {
        if (window.ThemeEvents.variantUpdate) {
          section.addEventListener(window.ThemeEvents.variantUpdate, (e) => this._onVariantUpdate(e));
        }
        if (window.ThemeEvents.variantSelected) {
          section.addEventListener(window.ThemeEvents.variantSelected, (e) => this._onVariantSelected(e));
        }
      }

      this._interceptFormSubmit();
    }

    _onVariantSelected() {}

    _onVariantUpdate(event) {
      const resource = event.detail?.resource;
      if (!resource?.id) return;

      const newVariant = this._findVariant(resource.id);
      if (!newVariant) {
        this.variant = {
          id: resource.id,
          price: resource.price,
          available: resource.available,
          sku: resource.sku || '',
          externalVariantId: '',
          vatReliefEligible: false,
          vatReliefPairId: '',
          taxable: true,
          sellingPlanAllocations: [],
        };
      } else {
        this.variant = newVariant;
      }

      if (resource.price !== undefined) {
        this.variant.price = resource.price;
      }

      this._reconcileVariantChange();
    }

    /**
     * Everything that must re-run when the main variant changes, factored out of
     * _onVariantUpdate so the bundle seat picker (which has no theme variant-picker
     * to fire variant:update) can drive it directly via _selectSeat.
     */
    _reconcileVariantChange() {
      this._recalcPlanPrices();
      this._populatePlanGrid();
      this._updateSubscriptionTabVisibility();
      this._updateNudgeVisibility();

      this._updateAllPrices();
      this._checkRentalAvailability();

      // V3.9: Horizon clears the wallet button's `hidden` attribute on every variant:update, so
      // its visibility has to be re-asserted here or the guards above are defeated by the variant
      // picker. _populatePlanGrid only reconciles via _selectPlan when the new variant has plans.
      this._reconcileAcceleratedCheckout();

      // V3.7: the pair variant changed with the selection — warm its plan cache
      this._preparePairPlan();

      if (this.state.calendarInstance) {
        this.state.calendarInstance.clear();
        this._resetRentalInputs();
      }

      this.state.rentalQuickDays = 0;
      if (this.refs.durationRow) {
        this.refs.durationRow.querySelectorAll('.totalcommerce-duration-btn').forEach(btn => {
          btn.setAttribute('aria-checked', 'false');
        });
      }

      this._updateVatToggle();
      this._updateCheckoutBlocked();

      // Tell the fulfilment picker which variant is on screen now. Collection availability is per
      // variant, so a store that can supply one size may not be able to supply another. This
      // mirrors the `wuva:fulfilment:change` event the picker already dispatches the other way.
      document.dispatchEvent(
        new CustomEvent('wuva:variant:change', {
          detail: { externalVariantId: this.variant?.externalVariantId || '' },
        })
      );
    }

    _updateVatToggle() {
      if (!this.refs.vatBanner) return;
      if (this._hasVatPair()) {
        this.refs.vatSection.style.display = '';
      } else {
        this.refs.vatSection.style.display = 'none';
        this.state.vatRelief = false;
        if (this.refs.vatToggle) this.refs.vatToggle.dataset.on = 'false';
        if (this.refs.vatBanner) this.refs.vatBanner.dataset.active = 'false';
        if (this.refs.vatFormWrap) this.refs.vatFormWrap.dataset.open = 'false';
        this._updateAllPrices();
      }
    }

    /* ======================================================================
       FETCH ACCURATE PLAN PRICES
       ====================================================================== */

    async _fetchAccuratePlanPrices() {
      try {
        const resp = await fetch(`/products/${this.config.productHandle}.js`);
        const product = await resp.json();
        const variant = product.variants.find(v => v.id == this.variant?.id);
        if (variant?.selling_plan_allocations) {
          variant.selling_plan_allocations.forEach(alloc => {
            this.planPrices[alloc.selling_plan_id] = alloc.per_delivery_price;
          });
          this._updateAllPrices();
        }
      } catch (e) {
        // Silently fail — calculated prices still work
      }
    }
  }

  /* ========================================================================
     5. INIT
     ======================================================================== */

  function init() {
    document.querySelectorAll('.totalcommerce-widget[data-section-id]').forEach(container => {
      const sectionId = container.dataset.sectionId;
      if (sectionId && !container.dataset.totalcommerceInit) {
        container.dataset.totalcommerceInit = 'true';
        new WuvaWidget(sectionId);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();